import { ViewportScroller } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, timer } from 'rxjs';
import {
  PositionV2Dto,
  POIDto,
  GeoCoordinateDto,
  BoundaryDto,
  CoverageMapSVGDto,
  EnterpriseCustomerIdDto,
  EventV2Dto,
  FieldV2Dto,
  InterpolationMapSVGDto,
  LocationDatasetV2Dto,
  LocationDataV2Dto,
  LocationV2Dto,
  MachineIdDto,
  MachineLocationSetV2Dto,
  MachineV2Dto,
} from 'src/app/api/exa';
import { GeometryDto } from 'src/app/api/exa/model/geometryDto';
import { BaseDataComponent } from 'src/app/core/components/base-data.component';
import { EventTypeEnum } from 'src/app/enums/event-type-enum';
import { LocationTypeEnum } from 'src/app/enums/location-type-enum';
import { MarkerTypeEnum } from 'src/app/enums/marker-type-enum';
import { HelperFunctions } from 'src/app/helper/helper-functions';
import { AppCacheService } from 'src/app/services/app-cache.service';
import { ColorService } from 'src/app/services/color.service';
import { LogService } from 'src/app/services/log.service';

export enum GeometryAddMode {
  DISABLED = 'DISABLED',
  ALL = 'ALL',
  FIELD = 'FIELD',
  LOCATION = 'LOCATION',
}

export enum GeometryEditStatus {
  INIT = 'INIT',
  START = 'STA',
  POLYGON_ADD_START = 'POLY_ADD_STA',
  POLYGON_ADD_FINISH = 'POLY_ADD_FIN',
  POLYGON_ADD_CANCEL = 'POLY_ADD_CAN',
  POLYGON_UPDATE = 'POLY_UPD',
  POLYGON_POINT_REMOVE = 'POLY_PT_RM',
  POLYGON_REMOVE = 'POLY_RM',
  POI_ADD_START = 'POI_ADD_STA',
  POI_ADD_FINISH = 'POI_ADD_FIN',
  POI_ADD_CANCEL = 'POI_ADD_CAN',
  POI_UPDATE = 'POI_UPD',
  POI_REMOVE = 'POI_RM',
  CANCEL = 'CAN',
  FINISH = 'FIN',
}

export enum CustomerMarkerType {
  PIN = 'PIN',
  CIRCULAR = 'CIRCULAR',
}

export interface MapOptions {
  mapId?: string; // only has to be set when another map style has to be used
  mapType?: google.maps.MapTypeId;
  mapTypeControl?: boolean;
  fullscreenControl?: boolean;
  scrollwheelZoom?: boolean;
  zoomControl?: boolean;
  markerInfoWindows?: boolean; // enabled/disable the extended marker info windows
  padding?: google.maps.Padding;
  zoom?: number;
  geometryMarkerHideZoomLevel?: number; // below this zoomlevel all marker are shown, above all are hidden; if undefined -> defaults to 15, if negative value -> disabled
  center?: google.maps.LatLngLiteral;
  geometryAddMode?: GeometryAddMode;
  disableGeometryEdit?: boolean;
  showGeometryPOIsOverride?: boolean; // shows the POIs of the geometries directly instead of 'activating' them by clicking on the geometry
  showHideRoadsButton?: boolean; // only available if mapType is 'HYBRID' and mapTypeControl = false
  showHideAllTracksButton?: boolean;
  showHideAllGeometriesButton?: boolean;
  showHideAllOverlaysButton?: boolean;
  showHideAllCustomMarkerButton?: boolean;
}

export interface Track {
  id: number; // e.g. machineId
  activated: boolean; // when clicked
  options: TrackOptions;
  marker?: google.maps.marker.AdvancedMarkerElement;
  markerListener?: google.maps.MapsEventListener;
  paths: google.maps.Polyline[];
  polylineListener: google.maps.MapsEventListener[];
  positions: TrackSlice[];
}

export interface TrackOptions {
  visible: boolean;
  clickable: boolean;
  hoverable: boolean;
  directionsVisible: boolean;
  markerVisible: boolean;
  markerNameVisible: boolean;
  showSliceColors: boolean;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  offline: boolean;
}

export interface TrackPosition {
  lat: number;
  lng: number;
  time?: number;
}

export interface TrackSlice {
  path: TrackPosition[];
  color?: string;
}

export interface TrackHover {
  id: number;
  position: TrackPosition;
}

export interface MarkerClick {
  id: number;
  active?: boolean;
}

export interface GeometryMarkerClick {
  id: string;
  id_orig: number;
  baseItem?: GeometryDto;
  active?: boolean;
}

export interface CustomMarkerOptions {
  visible: boolean;
  clickable: boolean;
  label?: string;
  data?: string;
  icon?: string;
  color?: string;
  visibleAllZoomLevels?: boolean;
  type: CustomerMarkerType;
  size?: number; // can only be used in conjunction with CustomerMarkerType.CIRCULAR and no icon
  position: PositionV2Dto;
  zIndex?: number;
  baseItem?: any;
}

export interface CustomMarker {
  id: number;
  activated: boolean; // when clicked
  clickable: boolean;
  visible: boolean;
  visibleAllZoomLevels: boolean;
  type: CustomerMarkerType;
  color?: string;
  size?: number;
  zIndex: number;
  marker: google.maps.marker.AdvancedMarkerElement;
  markerListener?: google.maps.MapsEventListener;
  baseItem?: any;
}

export interface CustomMarkerClick {
  id: number;
  id_orig: number;
  active?: boolean;
  baseItem?: any;
}

export interface Geometry {
  id: string;
  activated: boolean; // when clicked
  visible: boolean;
  fillOpacity: number;
  strokeOpacity: number;
  color: string;
  centroidMarker?: google.maps.marker.AdvancedMarkerElement;
  centroidMarkerVisible: boolean;
  boundaries: google.maps.Polygon[];
  pois: POI[];
  poisVisible: boolean;
  clickable: boolean;
  boundaryListeners: google.maps.MapsEventListener[];
  markerListeners: google.maps.MapsEventListener[];
  baseItem: GeometryDto;
}

export interface POI {
  id: number;
  activated: boolean; // when clicked
  marker: google.maps.marker.AdvancedMarkerElement;
  baseItem: POIDto;
}

export interface GeometryChanged {
  id: string;
  geometry: GeometryDto;
  isField: boolean;
}

export interface GeometryEditStatusChanged {
  id: string;
  status: GeometryEditStatus;
}

export interface SVGOverlayOptions {
  map?: google.maps.Map;
  lowerRight: PositionV2Dto;
  upperLeft: PositionV2Dto;
  svgData: string;
  opacity?: number;
  visible: boolean;
}

export interface SVGOverlay {
  id: number;
  visible: boolean;
  overlay: ExaSVGOverlay;
}

export class ExaSVGOverlay extends google.maps.OverlayView {
  bounds_: google.maps.LatLngBounds;
  svgHTML_: ChildNode;
  map_?: google.maps.Map;
  div_?: HTMLElement;
  opacity_: number;

  constructor(svgOverlayOptions: SVGOverlayOptions) {
    super();
    // Initialize all properties.
    const northeast = new google.maps.LatLng(svgOverlayOptions.upperLeft.latitude ?? 0, svgOverlayOptions.lowerRight.longitude);
    const southwest = new google.maps.LatLng(svgOverlayOptions.lowerRight.latitude ?? 0, svgOverlayOptions.upperLeft.longitude);
    this.bounds_ = new google.maps.LatLngBounds(southwest, northeast);
    this.svgHTML_ = this.parseSVG(svgOverlayOptions.svgData);
    this.map_ = svgOverlayOptions.map;
    // Define a property to hold the image's div. We'll
    // actually create this div upon receipt of the onAdd()
    // method so we'll leave it null for now.
    this.div_ = undefined;
    this.opacity_ = 0.8;
    if (svgOverlayOptions.opacity) {
      this.opacity_ = svgOverlayOptions.opacity;
    }
    if (this.map_) {
      // Explicitly call setMap on this overlay.
      this.setMap(this.map_);
    }
  }

  /**
   * onAdd is called when the map's panes are ready and the overlay has been
   * added to the map.
   */
  override onAdd() {
    const div = document.createElement('div');
    div.style.borderStyle = 'none';
    div.style.borderWidth = '0px';
    div.style.position = 'absolute';
    div.style.opacity = this.opacity_.toString();
    div.appendChild(this.svgHTML_);
    this.div_ = div;
    // Add the element to the 'overlayLayer' pane.
    const panes = this.getPanes();
    if (panes) {
      panes.overlayLayer.appendChild(div);
    }
  }

  // The onRemove() method will be called automatically from the API if
  // we ever set the overlay's map property to 'null'.
  override onRemove() {
    if (this.div_) {
      this.div_.parentNode?.removeChild(this.div_);
    }
    this.div_ = undefined;
  }

  override draw() {
    // We use the south-west and north-east
    // coordinates of the overlay to peg it to the correct position and size.
    // To do this, we need to retrieve the projection from the overlay.
    const overlayProjection = this.getProjection();
    // Retrieve the south-west and north-east coordinates of this overlay
    // in LatLngs and convert them to pixel coordinates.
    // We'll use these coordinates to resize the div.
    const sw = overlayProjection.fromLatLngToDivPixel(this.bounds_.getSouthWest());
    const ne = overlayProjection.fromLatLngToDivPixel(this.bounds_.getNorthEast());
    const div = this.div_;
    if (sw && ne && div) {
      // Resize the image's div to fit the indicated dimensions.
      div.style.left = sw.x.toString() + 'px';
      div.style.top = ne.y.toString() + 'px';
      div.style.width = (ne.x - sw.x).toString() + 'px';
      div.style.height = (sw.y - ne.y).toString() + 'px';
      div.style.opacity = this.opacity_.toString();
    }
  }

  setOpacity(opacity: number) {
    if (opacity > 1) {
      opacity = 1;
    } else if (opacity < 0) {
      opacity = 0;
    }
    this.opacity_ = opacity;
    if (this.div_) {
      const div = this.div_;
      div.style.opacity = opacity.toString();
    }
  }

  private parseSVG(svgString: string): ChildNode {
    // parse the svg string and return the svg object
    // -> parseFromString creates complete HTML object with head, body and so on
    return new DOMParser().parseFromString(svgString, 'text/html').body.childNodes[0];
  }
}

@Component({
  selector: 'app-exa-new-map',
  templateUrl: './exa-new-map.component.html',
  styleUrls: ['./exa-new-map.component.scss'],
})
export class ExaNewMapComponent extends BaseDataComponent {
  public static GEOMETRY_FILL_DEFAULT = 0.3;
  public static GEOMETRY_STROKE_DEFAULT = 0.8;
  public static OFFLINE_MARKER_COLOR = '#bbbec2';
  public static DEFAULT_COLOR = ColorService.DEFAULT_COLOR;
  public static OWN_POSITION_MAKER_ID = -1;

  protected buttonDisabledClass = 'disabled';

  id = 0;

  @Input() set loading(isLoading: boolean) {
    this.setLoading(isLoading);
  }

  _options: MapOptions | undefined;
  @Input() set options(options: MapOptions) {
    // generate random ID for divs to add and hide
    // otherwise there is a problem with two or more parallel maps
    // 'random' -> take the current timestamp
    this.id = new Date().getTime();

    this._options = options;

    if (this._options.markerInfoWindows == undefined) {
      this._options.markerInfoWindows = true;
    }
    if (this._options.showGeometryPOIsOverride == undefined) {
      this._options.showGeometryPOIsOverride = false;
    }
    if (this._options.mapId) {
      this.mapOptions.mapId = this._options.mapId;
    }
    if (this._options.zoomControl != undefined) {
      this.mapOptions.zoomControl = this._options.zoomControl;
    }
    if (this._options.mapType != undefined) {
      this.mapOptions.mapTypeId = this._options.mapType;
      if (this._options.mapType == google.maps.MapTypeId.HYBRID) {
        this._options.showHideRoadsButton = true;
      } else {
        this._options.showHideRoadsButton = false;
      }
    }
    if (this._options.mapTypeControl != undefined) {
      this.mapOptions.mapTypeControl = this._options.mapTypeControl;
      this._options.showHideRoadsButton = false;
    }
    if (this._options.showHideRoadsButton == undefined) {
      this._options.showHideRoadsButton = true;
    }
    if (this._options.scrollwheelZoom != undefined) {
      this.mapOptions.scrollwheel = this._options.scrollwheelZoom;
    }
    if (this._options.zoom != undefined) {
      this.mapOptions.zoom = this._options.zoom;
    }
        if (this._options.geometryMarkerHideZoomLevel != undefined) {
      if (this._options.geometryMarkerHideZoomLevel < 0) {
        this.geometryMarkerHideZoomLevel = undefined;
      } else {
        this.geometryMarkerHideZoomLevel = this._options.geometryMarkerHideZoomLevel;
      }
    } else {
      this.geometryMarkerHideZoomLevel = this.geometryMarkerHideZoomLevelDefault;
    }

    if (this._options.center != undefined) {
      this.mapOptions.center = this._options.center;
    }
  }

  // @Input() ngxScrollbar?: NgScrollbar;

  @Output() mapInitialized: EventEmitter<ExaNewMapComponent> = new EventEmitter();

  @Output() typeChanged: EventEmitter<string | undefined> = new EventEmitter();
  @Output() centerChanged: EventEmitter<GeoCoordinateDto | undefined> = new EventEmitter();
  @Output() zoomChanged: EventEmitter<number | undefined> = new EventEmitter();
  @Output() boundsChanged: EventEmitter<GeoCoordinateDto[] | undefined> = new EventEmitter();

  @Output() trackHovered: EventEmitter<TrackHover> = new EventEmitter();
  @Output() machineActiveChanged: EventEmitter<MarkerClick> = new EventEmitter();
  @Output() machineClicked: EventEmitter<MarkerClick> = new EventEmitter();
  @Output() geometryActiveChanged: EventEmitter<GeometryMarkerClick> = new EventEmitter();
  @Output() geometryClicked: EventEmitter<GeometryMarkerClick> = new EventEmitter();
  @Output() customMarkerActiveChanged: EventEmitter<MarkerClick> = new EventEmitter();
  @Output() customMarkerClicked: EventEmitter<CustomMarkerClick> = new EventEmitter();

  @Output() geometryChanged: EventEmitter<GeometryChanged> = new EventEmitter();
  @Output() geometryEditStatusChanged: EventEmitter<GeometryEditStatusChanged> = new EventEmitter();

  @Output() fullscreenChanged: EventEmitter<boolean> = new EventEmitter();

  protected mapOptions: google.maps.MapOptions = {
    //renderingType: google.maps.RenderingType.RASTER,
    mapId: 'b2b45549b4f4edf', //-> https://console.cloud.google.com/google/maps-apis/studio/maps?authuser=1&project=exatrek
    mapTypeId: google.maps.MapTypeId.HYBRID,
    mapTypeControl: false,
    scrollwheel: true,
    disableDefaultUI: true,
    //mapTypeControlOptions: { position: google.maps.ControlPosition.TOP_CENTER },
    fullscreenControl: false,
    rotateControl: false,
    zoomControl: false,
    tilt: 0,
    streetViewControl: false,
    tiltInteractionEnabled: false,
    headingInteractionEnabled: false,
    center: { lat: 51.163375, lng: 10.447683 },
    zoom: 6,
    minZoom: 3,
    restriction: {
      latLngBounds: {
        north: 85,
        south: -85,
        east: 180,
        west: -180,
      },
      strictBounds: true,
    },
    gestureHandling: 'greedy',
  };

  public map?: google.maps.Map;

  private tracks: Map<number, Track> = new Map();
  private defaultTrackArrowIcon: google.maps.IconSequence = {
    fixedRotation: false,
    repeat: '110px',
    icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, fillOpacity: 1, scale: 2 },
  };
  private currentlyActiveTrack?: Track;

  private geometries: Map<string, Geometry> = new Map();
  private currentlyActiveGeometry?: Geometry;
  private currentlyActiveGeometryPOI?: POI;

  private svgOverlays: Map<number, SVGOverlay> = new Map();

  private customMarker: Map<number, CustomMarker> = new Map();
  private currentlyActiveCustomMarker?: CustomMarker;

  private manipulatingGeometry = false;
  private editingGeometryId?: string;
  private editingGeometryIsField?: boolean;
  private editingGeometryBoundaries?: google.maps.Polygon[];
  private editingGeometryBoundariesListener?: google.maps.MapsEventListener[];
  private editingGeometryDeleteMarker?: google.maps.marker.AdvancedMarkerElement[];
  private editingGeometryDeleteMarkerListener?: google.maps.MapsEventListener[];
  private editingGeometryPOIs?: POI[];
  private editingGeometryPOIsListener?: google.maps.MapsEventListener[];
  private unfinishedPolygon?: google.maps.Polyline;
  private unfinishedPolygonMarker?: google.maps.marker.AdvancedMarkerElement[];
  private unfinishedPolygonMarkerListener?: google.maps.MapsEventListener[];
  private unfinishedPolygonListener?: google.maps.MapsEventListener[];
  private addingPOI = false;
  private addingPOIData?: POIDto;

  private foregroundZIndex = 1000000;
  private ownPositionZIndex = 20000;
  private machineBaseZIndex = 5000;
  private machineOnlineZIndex = 5000;
  private geometryZIndex = 2;

  private lastPOIId = 0;
  private lastOverlayId = 0;
  private lastCustomMarkerId = 0;
  private lastGeometryBasicId = 0;

  private geometryMarkerHideZoomLevelDefault = 15;
  private geometryMarkerHideZoomLevel?: number;
  private currentZoom?: number;

  private addedMapControls: Map<google.maps.ControlPosition, number> = new Map();

  protected allTracksVisible = true;
  protected allGeometriesVisible = true;
  protected allOverlaysVisible = true;
  protected allCustomMarkerVisible = true;

  @ViewChild('mapWrapper') mapWrapper?: ElementRef;
  protected isFullscreen = false;

  private svgIconMap: Map<string, string> = new Map();
  private machineMarkerMap: Map<number, HTMLElement> = new Map();
  private geometryCentroidMap: Map<string, HTMLElement> = new Map();

  isIOS: boolean;


  constructor(
    @Inject(String) private name: string = 'MapBaseComponent',
    private _logService: LogService,
    private _translateService: TranslateService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _cache: AppCacheService,
    private _sanitizer: DomSanitizer,
    private httpClient: HttpClient,
    private platform: Platform,
    private viewportScroller: ViewportScroller
  ) {
    super(name, _logService, _translateService, _changeDetectorRef);
    this.setLoading(true);
     if (this.platform.is('ios')) {
      this.isIOS = true;
    } else {
      this.isIOS = false;
    }
  }

  mapInit(map: google.maps.Map) {
    this.map = map;
    this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
      this.mapClickHandler(event);
    });

    this.map.addListener('maptypeid_changed', () => {
      this.mapTypeChanged();
    });
    this.map.addListener('center_changed', () => {
      this.mapCenterChanged();
    });
    this.map.addListener('zoom_changed', () => {
      this.mapZoomChanged();
    });
    this.map.addListener('bounds_changed', () => {
      this.mapBoundsChanged();
    });

    const mapButtonsFullscreen = document.getElementById('map-buttons-fullscreen-' + this.id.toString());
    if (mapButtonsFullscreen) {
      this.addElementToMap(google.maps.ControlPosition.RIGHT_TOP, mapButtonsFullscreen);
    }

    const mapButtons = document.getElementById('map-buttons-' + this.id.toString());
    if (mapButtons) {
      this.addElementToMap(google.maps.ControlPosition.RIGHT_TOP, mapButtons);
    }
    const mapButtonsTop = document.getElementById('map-buttons-top-' + this.id.toString());
    if (mapButtonsTop) {
      this.addElementToMap(google.maps.ControlPosition.TOP_CENTER, mapButtonsTop);
    }

    this.fitBounds();

    this.setLoading(false);
    this.mapInitialized.emit(this);
  }

  override onDestroy() {
    this.clear();
    this.clearAddedElements();
    this.map = undefined;
  }

  getWrapperElement(): ElementRef | undefined {
    return this.mapWrapper;
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.viewportScroller.scrollToPosition([0, 0]);
      // if (this.ngxScrollbar) {
      //   void this.ngxScrollbar.scrollTo({ top: 0, duration: 0 });
      // }
    }

    this.fullscreenChanged.emit(this.isFullscreen);
  }

  private clearAddedElements() {
    if (this.map) {
      // remove the last X elements from the specific control
      for (const control of this.addedMapControls.keys()) {
        const elementsToRemove = this.addedMapControls.get(control);
        if (elementsToRemove) {
          for (let i = 0; i < elementsToRemove; i++) {
            this.map.controls[control].removeAt(this.map.controls[control].getLength() - 1);
          }
        }
      }
    }
    this.addedMapControls.clear();
  }

  clear() {
    for (const track of this.tracks.values()) {
      this.removeTrack(track);
    }
    this.tracks.clear();
    for (const geometry of this.geometries.values()) {
      this.removeGeometry(geometry);
    }
    this.geometries.clear();
    for (const overlay of this.svgOverlays.values()) {
      this.removeOverlay(overlay);
    }
    this.svgOverlays.clear();
    // reset buttons
    this.resetGeometryEditHelper();
  }

  private checkForMap() {
    if (!this.map) {
      throw new Error('map not initialized');
    }
  }

  setGestures(active: boolean) {
    this.checkForMap();
    this.map?.setOptions({ draggable: active, zoomControl: active, scrollwheel: active, disableDoubleClickZoom: !active });
  }

  getCenter(): google.maps.LatLng | undefined {
    this.checkForMap();
    return this.map?.getCenter();
  }

  setCenter(center: google.maps.LatLng | google.maps.LatLngLiteral, pan?: boolean) {
    this.checkForMap();
    if (pan) {
      this.map?.panTo(center);
    } else {
      this.map?.setCenter(center);
    }
  }

  getZoom(): number | undefined {
    this.checkForMap();
    return this.map?.getZoom();
  }

  setZoom(zoom: number) {
    this.checkForMap();
    this.map?.setZoom(zoom);
  }

  fitBounds(includeCompleteTracks?: boolean, padding = 50) {
    // fits bounds around every element stored in here
    let bounds: google.maps.LatLngBoundsLiteral | null = null;
    // 1) add all track marker points
    for (const track of this.tracks.values()) {
      if (track.marker && track.marker.position) {
        bounds = this.addToBounds(bounds, track.marker.position);
      }
      // add all track positions if needed
      if (includeCompleteTracks && track.paths) {
        for (const path of track.paths) {
          for (const pos of path.getPath().getArray()) {
            bounds = this.addToBounds(bounds, pos);
          }
        }
      }
    }
    // 2) add all geometry boundary points
    for (const geometry of this.geometries.values()) {
      if (geometry.boundaries) {
        for (const boundary of geometry.boundaries) {
          for (const pos of boundary.getPath().getArray()) {
            bounds = this.addToBounds(bounds, pos);
          }
        }
      }
    }
    if (bounds) {
      this.setBounds(bounds, padding);
    } else {
      // fallback to 'GERMANY'
      this.fitBoundsGeometriesGermany();
    }
  }

  fitBoundsTracks(includeCompleteTracks?: boolean, padding = 50) {
    // fits bounds around every element stored in here
    let bounds: google.maps.LatLngBoundsLiteral | null = null;
    // add all track marker points
    for (const track of this.tracks.values()) {
      if (track.marker && track.marker.position) {
        bounds = this.addToBounds(bounds, track.marker.position);
      }
      // add all track positions if needed
      if (includeCompleteTracks && track.paths) {
        for (const path of track.paths) {
          for (const pos of path.getPath().getArray()) {
            bounds = this.addToBounds(bounds, pos);
          }
        }
      }
    }
    if (bounds) {
      this.setBounds(bounds, padding);
    } else {
      // fallback to geometries
      this.fitBoundsGeometries();
    }
  }

  fitBoundsGeometries(padding = 50) {
    // fits bounds around every element stored in here
    let bounds: google.maps.LatLngBoundsLiteral | null = null;
    // add all geometry boundary points
    for (const geometry of this.geometries.values()) {
      if (geometry.boundaries) {
        for (const boundary of geometry.boundaries) {
          for (const pos of boundary.getPath().getArray()) {
            bounds = this.addToBounds(bounds, pos);
          }
        }
      }
    }
    if (bounds) {
      this.setBounds(bounds, padding);
    } else {
      this.fitBoundsGeometriesGermany();
    }
  }

  private fitBoundsGeometriesGermany() {
    const bounds: google.maps.LatLngBoundsLiteral = {
      north: 55.05,
      south: 47.27,
      west: 5.87,
      east: 15.03,
    };
    this.setBounds(bounds);
  }

  private addToBounds(
    bounds: google.maps.LatLngBoundsLiteral | null,
    position: google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral | null
  ) {
    if (position) {
      if (position instanceof google.maps.LatLng) {
        position = position.toJSON();
      }
      if (!bounds) {
        bounds = {
          north: position.lat,
          south: position.lat,
          west: position.lng,
          east: position.lng,
        };
      }
      // update the bounds if needed
      if (position.lat > bounds.north) {
        bounds.north = position.lat;
      }
      if (position.lat < bounds.south) {
        bounds.south = position.lat;
      }
      if (position.lng < bounds.west) {
        bounds.west = position.lng;
      }
      if (position.lng > bounds.east) {
        bounds.east = position.lng;
      }
    }
    return bounds;
  }

  getBounds(): google.maps.LatLngBounds | undefined {
    this.checkForMap();
    return this.map?.getBounds();
  }

  setBounds(bounds: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral, padding?: number | google.maps.Padding, pan?: boolean) {
    this.checkForMap();
    if (pan) {
      this.map?.panToBounds(bounds, this.getPadding(padding));
    } else {
      this.map?.fitBounds(bounds, this.getPadding(padding));
    }
    this.currentZoom = this.map?.getZoom(); // workaround for showing/hiding geometry centroids on certain zoom levels
  }

  private getPadding(padding?: number | google.maps.Padding): undefined | number | google.maps.Padding {
    if (this._options && this._options.padding) {
      // add base map padding
      const padding_: google.maps.Padding = {
        bottom: this._options.padding.bottom,
        left: this._options.padding.left,
        right: this._options.padding.right,
        top: this._options.padding.top,
      };
      if (padding) {
        // add additional padding
        if (typeof padding === 'number') {
          // add equally to all previously added paddings
          if (padding_.bottom) {
            padding_.bottom += padding;
          } else {
            padding_.bottom = padding;
          }
          if (padding_.left) {
            padding_.left += padding;
          } else {
            padding_.left = padding;
          }
          if (padding_.right) {
            padding_.right += padding;
          } else {
            padding_.right = padding;
          }
          if (padding_.top) {
            padding_.top += padding;
          } else {
            padding_.top = padding;
          }
        } else {
          // add the specific paddings
          if (padding_.bottom && padding.bottom) {
            padding_.bottom += padding.bottom;
          } else if (!padding_.bottom && padding.bottom) {
            padding_.bottom = padding.bottom;
          }
          if (padding_.left && padding.left) {
            padding_.left += padding.left;
          } else if (!padding_.left && padding.left) {
            padding_.left = padding.left;
          }
          if (padding_.right && padding.right) {
            padding_.right += padding.right;
          } else if (!padding_.right && padding.right) {
            padding_.right = padding.right;
          }
          if (padding_.top && padding.top) {
            padding_.top += padding.top;
          } else if (!padding_.top && padding.top) {
            padding_.top = padding.top;
          }
        }
      }
      return padding_;
    } else {
      return padding;
    }
  }

  private mapClickHandler(event: google.maps.MapMouseEvent) {
    if (this.currentlyActiveTrack) {
      this.setTrackActive(this.currentlyActiveTrack, false);
    }
    if (this.currentlyActiveGeometry) {
      this._setGeometryActive(this.currentlyActiveGeometry, false);    }
    if (this.unfinishedPolygon && event.latLng) {
      this.addMarkerToUnfinishedPolygon(event.latLng);
    }
    if (this.addingPOI && event.latLng) {
      this.addPOIMarker(event.latLng);
    }
  }

  private mapTypeChanged() {
    this.typeChanged.emit(this.map?.getMapTypeId());
  }

  protected mapTypeIsHybrid(): boolean {
    return this.map?.getMapTypeId() == google.maps.MapTypeId.HYBRID;
  }

  private mapZoomChanged() {
    const lastZoom = this.currentZoom;
    this.currentZoom = this.map?.getZoom();
    this.zoomChanged.emit(this.currentZoom);
    if (this.currentZoom) {
      // only update on change -> e.g. 'zoom' needs hide but 'currentZoom' does not need it
      if (!lastZoom || this.showMarkerForZoom(this.currentZoom) != this.showMarkerForZoom(lastZoom)) {
        this.setGeometryCentroidsVisible(this.showMarkerForZoom(this.currentZoom));
        this.setGeometriesPOIsVisible(this.showMarkerForZoom(this.currentZoom));
      }
    }
  }

  private mapCenterChanged() {
    const center = this.map?.getCenter();
    let geoCenter: GeoCoordinateDto | undefined = undefined;
    if (center) {
      geoCenter = { latitude: center.lat(), longitude: center.lng() };
    }
    this.centerChanged.emit(geoCenter);
  }

  private mapBoundsChanged() {
    const bounds = this.map?.getBounds();
    if (bounds) {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const boundsGeo: GeoCoordinateDto[] = [
        { latitude: ne.lat(), longitude: ne.lng() },
        { latitude: sw.lat(), longitude: sw.lng() },
      ];

      this.boundsChanged.emit(boundsGeo);
    }
  }

  addButtonToMap(element: HTMLElement) {
    this.checkForMap();
    const mapButtons = document.getElementById('map-buttons-' + this.id.toString());
    if (mapButtons) {
      mapButtons.insertBefore(element, mapButtons.firstChild);
    }
  }

  addElementToMap(position: google.maps.ControlPosition, element: HTMLElement) {
    this.checkForMap();
    this.map?.controls[position].push(element);
    if (!this.addedMapControls.has(position)) {
      this.addedMapControls.set(position, 0);
    }
    this.addedMapControls.set(position, (this.addedMapControls.get(position) ?? 0) + 1);
  }

  private showMarkerForZoom(zoom?: number): boolean {
    if (zoom != undefined && this.geometryMarkerHideZoomLevel) {
      return zoom > this.geometryMarkerHideZoomLevel;

    }
    return true;
  }

  private markerInfoWindowsEnabled(): boolean {
    return !this._options || this._options.markerInfoWindows == undefined || this._options.markerInfoWindows;
  }

  private showGeometryPOIsOverrideEnabled(): boolean {
    return !this._options || this._options.showGeometryPOIsOverride == undefined || this._options.showGeometryPOIsOverride;
  }

  private getTrack(id: number): Track {
    const track = this.tracks.get(id);
    if (track) {
      return track;
    } else {
      throw new Error('no track with id ' + id.toString());
    }
  }

  private getGeometry(id: string): Geometry {
    const geo = this.geometries.get(id);
    if (geo) {
      return geo;
    } else {
      throw new Error('no geometry with id ' + id.toString());
    }
  }

  private getOverlay(id: number): SVGOverlay {
    const overlay = this.svgOverlays.get(id);
    if (overlay) {
      return overlay;
    } else {
      throw new Error('no overlay with id ' + id.toString());
    }
  }

  private getCustomMarker(id: number): CustomMarker {
    const marker = this.customMarker.get(id);
    if (marker) {
      return marker;
    } else {
      throw new Error('no marker with id ' + id.toString());
    }
  }

  private generateUniquePOIId(): number {
    this.lastPOIId++;
    return this.lastPOIId;
  }

  private generateUniqueOverlayId(): number {
    this.lastOverlayId++;
    return this.lastOverlayId;
  }

  private generateUniqueCustomMarkerId(): number {
    this.lastCustomMarkerId++;
    return this.lastCustomMarkerId;
  }

  private addTrack_(id: number, options: TrackOptions, markerContent?: HTMLElement, paths?: TrackSlice[]) {
    this.checkForMap();
    if (this.tracks.has(id)) {
      if (paths) {
        this.addSlicesToTrack(id, paths);
        return;
      }
    }

    let zIndex = id;
    if (zIndex < 0) {
      zIndex = zIndex * -1;
    }
    // push to front -> over geometries and so on
    zIndex += this.machineBaseZIndex;
    if (!options.offline) {
      // push even more to the front when online
      zIndex += this.machineOnlineZIndex;
    }

    options.markerVisible = options.markerVisible && this.allTracksVisible;
    options.visible = options.visible && this.allTracksVisible;

    let lastPos: google.maps.LatLngLiteral | null = null;
    if (paths && paths.length > 0) {
      const lastSlice: TrackPosition[] = paths[paths.length - 1].path;
      if (lastSlice.length > 0) {
        lastPos = { lat: lastSlice[lastSlice.length - 1].lat, lng: lastSlice[lastSlice.length - 1].lng };
      }
    }

    let marker: google.maps.marker.AdvancedMarkerElement | undefined = undefined;
    let markerListener: google.maps.MapsEventListener | undefined = undefined;
    if (markerContent) {
      const maOpts: google.maps.marker.AdvancedMarkerElementOptions = {
        map: options.markerVisible ? this.map : null,
        zIndex: zIndex + 1,
        position: lastPos,
        content: markerContent,
      };
      marker = new google.maps.marker.AdvancedMarkerElement(maOpts);
      markerListener = marker.addListener('click', () => {
        this.trackMarkerClickHandler(id);
      });
    }

    const polyPaths: google.maps.Polyline[] = [];
    const polylineListener: google.maps.MapsEventListener[] = [];

    let trackDirectionIcon: google.maps.IconSequence[] | null = null;
    if (options.directionsVisible) {
      trackDirectionIcon = [this.defaultTrackArrowIcon];
    }

    // create track slices
    if (paths) {
      // convert GeoCoordinate to LatLngLiteral
      for (const path of paths) {
        const path_: google.maps.LatLngLiteral[] = [];
        for (const coord of path.path) {
          path_.push({ lat: coord.lat, lng: coord.lng });
        }
        let color = options.strokeColor;
        if (options.showSliceColors && path.color) {
          color = path.color;
        }
        const polyOpts: google.maps.PolylineOptions = {
          clickable: true, // otherwise no hover
          draggable: false,
          editable: false,
          geodesic: false,
          icons: trackDirectionIcon,
          map: options.visible ? this.map : null,
          path: path_,
          strokeColor: color,
          strokeOpacity: options.strokeOpacity,
          strokeWeight: options.strokeWeight,
          visible: options.visible,
          zIndex: zIndex,
        };
        const polyline: google.maps.Polyline = new google.maps.Polyline(polyOpts);
        polyPaths.push(polyline);
        const polylineListener_: google.maps.MapsEventListener = polyline.addListener('mouseover', (event: google.maps.PolyMouseEvent) => {
          this.trackHoverHandler(id, event);
        });
        polylineListener.push(polylineListener_);
      }
    }

    const track: Track = {
      id: id,
      activated: false,
      options: options,
      marker: marker,
      markerListener: markerListener,
      paths: polyPaths,
      polylineListener: polylineListener,
      positions: paths ?? [],
    };
    this.tracks.set(id, track);
  }

  hasTrack(id: number): boolean {
    try {
      this.getTrack(id);
      return true;
    } catch (e) {
      return false;
    }
  }

  addTrackWithoutMachineBasic(locations: GeoCoordinateDto[], visible: boolean): number {
    const slices: TrackSlice[] = [
      {
        color: ExaNewMapComponent.DEFAULT_COLOR,
        path: this.mapLocationsGeoCoordinate(locations),
      },
    ];
    return this.addTrackWithoutMachine(slices, false, false, undefined, visible);
  }

  addTrackWithoutMachine(
    slices: TrackSlice[],
    directionsVisible: boolean,
    showSliceColors: boolean,
    color = ExaNewMapComponent.DEFAULT_COLOR,
    visible = true
  ): number {
    const options: TrackOptions = {
      visible: visible,
      clickable: true,
      hoverable: false,
      directionsVisible: directionsVisible,
      markerVisible: false,
      markerNameVisible: false,
      showSliceColors: showSliceColors,
      strokeColor: color,
      strokeOpacity: undefined, // use google maps default
      strokeWeight: undefined, // use google maps default,
      offline: false,
    };
    const id = new Date().getTime() * -1;
    this.addTrack_(id, options, undefined, slices);
    return id;
  }

  addTrackMachineLocations(
    machine: MachineV2Dto | MachineIdDto,
    locationSet: LocationDatasetV2Dto[],
    trackVisible: boolean,
    directionsVisible: boolean,
    markerVisible: boolean,
    showSliceColors: boolean,
    gnssSourceColor?: boolean,
    markerNameVisible = true
  ): number {
    const slices: TrackSlice[] = [];
    for (const locations of locationSet) {
      if (locations.locations) {
        slices.push({
          color: gnssSourceColor ? this.getColorGNSSSource(locations.locations[0].gnssSource) : this.getColorWorkstate(locations.workstate),
          path: this.mapLocations(locations.locations),
        });
      }
    }
    return this.addTrackMachine(machine, trackVisible, directionsVisible, markerVisible, showSliceColors, slices, markerNameVisible);
  }

  addTrackMachineLocationSet(
    locationSet: MachineLocationSetV2Dto,
    trackVisible: boolean,
    directionsVisible: boolean,
    markerVisible: boolean,
    showSliceColors: boolean,
    gnssSourceColor?: boolean,
    markerNameVisible = true
  ): number {
    if (locationSet.machine) {
      const slices: TrackSlice[] = [];
      if (locationSet.locations) {
        for (const locations of locationSet.locations) {
          if (locations.locations) {
            slices.push({
              color: gnssSourceColor
                ? this.getColorGNSSSource(locations.locations[0].gnssSource)
                : this.getColorWorkstate(locations.workstate),
              path: this.mapLocations(locations.locations),
            });
          }
        }
      }
      return this.addTrackMachine(
        locationSet.machine,
        trackVisible,
        directionsVisible,
        markerVisible,
        showSliceColors,
        slices,
        markerNameVisible
      );
    }
    throw new Error('no machine');
  }

  private mapLocations(locations: LocationDataV2Dto[] | PositionV2Dto[]): TrackPosition[] {
    const trackPos: TrackPosition[] = [];
    for (const loc of locations) {
      trackPos.push({ lat: loc.latitude ?? 0, lng: loc.longitude ?? 0, time: new Date(loc.time ?? 0).getTime() });
    }
    return trackPos;
  }

  private mapLocationsGeoCoordinate(locations: GeoCoordinateDto[]): TrackPosition[] {
    const trackPos: TrackPosition[] = [];
    for (let i = 0; i < locations.length; i++) {
      const loc = locations[i];
      trackPos.push({ lat: loc.latitude ?? 0, lng: loc.longitude ?? 0, time: i });
    }
    return trackPos;
  }

  addTrackMachine(
    machine: MachineV2Dto | MachineIdDto,
    trackVisible: boolean,
    directionsVisible: boolean,
    markerVisible: boolean,
    showSliceColors: boolean,
    track?: TrackSlice[],
    markerNameVisible = true,
    isOffline = false,
    showOwner = false
  ): number {
    if (machine.id && this.tracks.has(machine.id)) {
      // shortcut ;)
      this.addSlicesToTrack(machine.id, track ?? []);
      return machine.id;
    } else {
      if (!machine.id) {
        throw new Error('no machine id');
      }
      const options: TrackOptions = {
        visible: trackVisible,
        clickable: true,
        hoverable: false,
        directionsVisible: directionsVisible,
        markerVisible: markerVisible,
        markerNameVisible: markerNameVisible,
        showSliceColors: showSliceColors,
        strokeColor: this.getColor(machine),
        strokeOpacity: undefined, // use google maps default
        strokeWeight: undefined, // use google maps default
        offline: isOffline,
      };
      if (Object.prototype.hasOwnProperty.call(machine, 'lastPosition')) {
        const machine_ = machine as MachineV2Dto;
        if (!track && machine_.lastPosition) {
          track = [
            {
              color: this.getColor(machine),
              path: [
                {
                  lat: machine_.lastPosition.latitude ?? 0,
                  lng: machine_.lastPosition.longitude ?? 0,
                  time: new Date(machine_.lastPosition.time ?? 0).getTime(),
                },
              ],
            },
          ];
        }
      }
      const marker = this.createMachineMarker(machine, true, markerNameVisible, isOffline, showOwner);
      this.addTrack_(machine.id, options, marker, track);

      return machine.id;
    }
  }

  getTrackPositionsById(id: number): TrackSlice[] {
    const track = this.getTrack(id);
    return track.positions;
  }

  /**
   * Adds the positions to the last track slice
   */
  addToTrack(id: number, positions: TrackPosition[], updateMarker?: boolean) {
    const track = this.getTrack(id);
    if (track.positions.length > 0) {
      for (const position of positions) {
        track.paths[track.paths.length - 1].getPath().push(new google.maps.LatLng({ lat: position.lat, lng: position.lng }));
        track.positions[track.positions.length - 1].path.push(position);
      }
    } else {
      // no path available -> add a new one
      this.addSlicesToTrack(id, [{ path: positions }], false);
    }
    if (updateMarker) {
      this.setMarkerPosition(track);
    }
  }

  addSlicesToTrack(id: number, slices: TrackSlice[], updateMarker?: boolean) {
    const track = this.getTrack(id);

    let zIndex = id;
    if (zIndex < 0) {
      zIndex = zIndex * -1;
    }

    for (const slice of slices) {
      const path_: google.maps.LatLngLiteral[] = [];
      for (const coord of slice.path) {
        path_.push({ lat: coord.lat, lng: coord.lng });
      }
      let color = track.options.strokeColor;
      if (track.options.showSliceColors && slice.color) {
        color = slice.color;
      }
      const polyOpts: google.maps.PolylineOptions = {
        clickable: true, // otherwise no hover
        draggable: false,
        editable: false,
        geodesic: false,
        icons: [this.defaultTrackArrowIcon],
        map: this.map,
        path: path_,
        strokeColor: color,
        strokeOpacity: track.options.strokeOpacity,
        strokeWeight: track.options.strokeWeight,
        visible: track.options.visible,
        zIndex: zIndex,
      };
      const polyline: google.maps.Polyline = new google.maps.Polyline(polyOpts);
      track.paths.push(polyline);
      const polylineListener_: google.maps.MapsEventListener = polyline.addListener('mouseover', (event: google.maps.PolyMouseEvent) => {
        this.trackHoverHandler(id, event);
      });
      track.polylineListener.push(polylineListener_);

      track.positions.push(slice);
    }
    if (updateMarker) {
      this.setMarkerPosition(track);
    }
  }

  setTrackStrokeMachine(machine: MachineV2Dto, strokeWeight?: number, strokeOpacity?: number) {
    if (!machine.id) {
      return;
    }
    this.setTrackStroke(machine.id, strokeWeight, strokeOpacity);
  }

  setTrackStroke(id: number, strokeWeight?: number, strokeOpacity?: number) {
    if (!id) {
      return;
    }
    const track = this.getTrack(id);
    this.setTrackStroke_(track, strokeWeight, strokeOpacity);
  }

  private setTrackStroke_(track: Track, strokeWeight?: number, strokeOpacity?: number) {
    if (strokeWeight) {
      track.options.strokeWeight = strokeWeight;
    }
    if (strokeOpacity && strokeOpacity > 0 && strokeOpacity <= 1) {
      track.options.strokeOpacity = strokeOpacity;
    }
    // update polylines
    for (const path of track.paths) {
      path.setOptions({ strokeOpacity: track.options.strokeOpacity, strokeWeight: track.options.strokeWeight });
    }
  }

  setTrackMachine(machine: MachineV2Dto) {
    if (!machine.id) {
      return;
    }
    const track = this.getTrack(machine.id);
    // remove the marker and readd it -> easier than checking for changes ;)
    const marker = this.createMachineMarker(machine, true);
    if (track.marker?.content) {
      track.marker.content = marker;
    }
    this.machineMarkerMap.set(machine.id, marker);
  }

  setTrackMachineData(id: number, lastOnline: Date, tries = 20, delay = 100) {
    this.getTrack(id); // just to make sure the track exists
    if (tries > 0) {
      void this.setTrackMachineData_retryable(id, lastOnline, tries, delay);
    } else {
      const onlineElement: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-last-online-' + this.id.toString());
      if (onlineElement) {
        onlineElement.innerText = this.formatDate(lastOnline, 'dd.MM.yyyy HH:mm:ss');
      }
    }
  }

  private setTrackMachineData_retryable(id: number, lastOnline: Date, tries: number, delay: number): Promise<void> {
    if (tries == 0) {
      return Promise.reject('all tries failed');
    }
    return new Promise<void>((resolve, reject) => {
      const onlineElement: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-last-online-' + this.id.toString());
      if (onlineElement) {
        onlineElement.innerText = this.formatDate(lastOnline, 'dd.MM.yyyy HH:mm:ss');
        resolve();
      } else {
        // retry after delay
        void firstValueFrom(timer(delay)).then(() => {
          return this.setTrackMachineData_retryable(id, lastOnline, tries--, delay);
        });
      }
    });
  }

  setTrackMachineIcon(id: number, svgIcon: string, tries = 20, delay = 100) {
    this.getTrack(id); // just to make sure the track exists
    if (tries > 0) {
      void this.setTrackMachineIcon_retryable(id, svgIcon, tries, delay);
    } else {
      const icon: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-icon-' + this.id.toString());
      const iconDetails: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-icon-details-' + this.id.toString());
      if (icon && iconDetails) {
        icon.innerHTML = svgIcon;
        iconDetails.innerHTML = svgIcon;
      }
    }
  }

  private setTrackMachineIcon_retryable(id: number, svgIcon: string, tries: number, delay: number): Promise<void> {
    if (tries == 0) {
      return Promise.reject('all tries failed');
    }
    return new Promise<void>((resolve, reject) => {
      const icon: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-icon-' + this.id.toString());
      const iconDetails: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-icon-details-' + this.id.toString());
      if (icon && iconDetails) {
        icon.innerHTML = svgIcon;
        iconDetails.innerHTML = svgIcon;
        resolve();
      } else {
        // retry after delay
        void firstValueFrom(timer(delay)).then(() => {
          return this.setTrackMachineIcon_retryable(id, svgIcon, tries--, delay);
        });
      }
    });
  }

  setTrackMarkerState(id: number, isOffline: boolean, tries = 20, delay = 100) {
    this.getTrack(id); // just to make sure the track exists
    if (tries > 0) {
      void this.setTrackMarkerState_retryable(id, isOffline, tries, delay);
    } else {
      const markerElement: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-marker-' + this.id.toString());
      if (markerElement) {
        const track = this.getTrack(id);
        // only update if needed
        if (track.options.offline != isOffline) {
          let color = track.options.strokeColor!;
          if (isOffline) {
            color = ExaNewMapComponent.OFFLINE_MARKER_COLOR;
          }
          markerElement.style.color = color;
          markerElement.style.backgroundColor = color;
          track.options.offline = isOffline;
        }
      }
    }
  }

  private setTrackMarkerState_retryable(id: number, isOffline: boolean, tries: number, delay: number): Promise<void> {
    if (tries == 0) {
      return Promise.reject('all tries failed');
    }
    return new Promise<void>((resolve, reject) => {
      const markerElement: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-marker-' + this.id.toString());
      if (markerElement) {
        const track = this.getTrack(id);
        // only update if needed
        if (track.options.offline != isOffline) {
          let color = track.options.strokeColor!;
          if (isOffline) {
            color = ExaNewMapComponent.OFFLINE_MARKER_COLOR;
          }
          markerElement.style.color = color;
          markerElement.style.backgroundColor = color;
          track.options.offline = isOffline;
        }
        resolve();
      } else {
        // retry after delay
        void firstValueFrom(timer(delay)).then(() => {
          return this.setTrackMarkerState_retryable(id, isOffline, tries--, delay);
        });
      }
    });
  }

  setTrackMarkerName(id: number, hide: boolean, tries = 20, delay = 100) {
    this.getTrack(id); // just to make sure the track exists
    if (tries > 0) {
      void this.setTrackMarkerName_retryable(id, hide, tries, delay);
    } else {
      const label: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-name-' + this.id.toString());
      if (label) {
        if (hide) {
          label.classList.add('hidden');
        } else {
          label.classList.remove('hidden');
        }
      }
    }
  }

  private setTrackMarkerName_retryable(id: number, hide: boolean, tries: number, delay: number): Promise<void> {
    if (tries == 0) {
      return Promise.reject('all tries failed');
    }
    return new Promise<void>((resolve, reject) => {
      const label: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-name-' + this.id.toString());
      if (label) {
        if (hide) {
          label.classList.add('hidden');
        } else {
          label.classList.remove('hidden');
        }
        resolve();
      } else {
        // retry after delay
        void firstValueFrom(timer(delay)).then(() => {
          return this.setTrackMarkerName_retryable(id, hide, tries--, delay);
        });
      }
    });
  }

  setTrackMarkerLabelColor(id: number, color?: string, tries = 20, delay = 100) {
    this.getTrack(id); // just to make sure the track exists
    if (tries > 0) {
      void this.setTrackMarkerLabelColor_retryable(id, color, tries, delay);
    } else {
      const label: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-name-' + this.id.toString());
      if (label) {
        if (color) {
          label.style.color = color;
        } else {
          label.style.removeProperty('color');
        }
      }
    }
  }

  private setTrackMarkerLabelColor_retryable(id: number, color: string | undefined | null, tries: number, delay: number): Promise<void> {
    if (tries == 0) {
      return Promise.reject('all tries failed');
    }
    return new Promise<void>((resolve, reject) => {
      const label: HTMLElement | null = document.getElementById('machine-' + id.toString() + '-name-' + this.id.toString());
      if (label) {
        if (color) {
          label.style.color = color;
        } else {
          label.style.removeProperty('color');
        }
        resolve();
      } else {
        // retry after delay
        void firstValueFrom(timer(delay)).then(() => {
          return this.setTrackMarkerLabelColor_retryable(id, color, tries--, delay);
        });
      }
    });
  }

  private setMarkerPosition(track: Track, position?: google.maps.LatLng | google.maps.LatLngLiteral) {
    this.checkForMap();
    if (track && track.marker) {
      if (position) {
        track.marker.position = position;
        if (track.options.markerVisible && !track.marker.map) {
          // show on map if it should be shown ;)
          track.marker.map = this.map;
        }
      } else {
        // check if we have a last position and set the marker to this position
        if (
          track.paths &&
          track.paths.length > 0 &&
          track.paths[track.paths.length - 1].getPath() &&
          track.paths[track.paths.length - 1].getPath().getLength() > 0
        ) {
          track.marker.position = track.paths[track.paths.length - 1]
            .getPath()
            .getAt(track.paths[track.paths.length - 1].getPath().getLength() - 1);
          if (track.options.markerVisible && !track.marker.map) {
            // show on map if it should be shown ;)
            track.marker.map = this.map;
          }
        }
      }
    }
  }

  setTrackMarker(id: number, position: GeoCoordinateDto | PositionV2Dto) {
    const track = this.getTrack(id);
    this.setMarkerPosition(track, { lat: position.latitude ?? 0, lng: position.longitude ?? 0 });
  }

  pruneTrack(id: number, maxLength: number) {
    const track = this.getTrack(id);
    this.pruneTrackIfNeeded(track, maxLength);
  }

  private pruneTrackIfNeeded(track: Track, maxLength: number) {
    // first compute the sum of all positions and based on this the number of elements to remove
    let size = 0;
    for (const path of track.paths) {
      size += path.getPath().getLength();
    }
    let elementsToRemove = size - maxLength;
    // iterate through the paths and remove from the front
    while (elementsToRemove > 0) {
      const pathLength = track.paths[0].getPath().getLength();
      if (pathLength <= elementsToRemove) {
        // completely remove the track slice
        track.paths[0].setMap(null);
        track.paths.shift();
        elementsToRemove = elementsToRemove - pathLength;
      } else {
        // just remove the first element
        track.paths[0].getPath().removeAt(0);
        elementsToRemove--;
      }
    }
  }

  clearTracks(showMarker?: boolean) {
    for (const id of this.tracks.keys()) {
      this.clearTrackById(id, showMarker);
    }
  }

  clearTrackById(id: number, showMarker?: boolean) {
    const track = this.getTrack(id);
    this.clearTrack(track, showMarker);
  }

  private clearTrack(track: Track, showMarker?: boolean) {
    // remove all paths
    for (const polyListener of track.polylineListener) {
      polyListener.remove();
    }
    for (const polyline of track.paths) {
      polyline.setMap(null);
    }
    track.polylineListener = [];
    track.paths = [];
    track.positions = [];
    if (!showMarker) {
      // remove marker from map since there are no positions to show
      if (track.marker) {
        track.marker.map = null;
      }
    }
  }

  removeTracks() {
    for (const id of this.tracks.keys()) {
      this.removeTrackById(id);
    }
  }

  removeTrackById(id: number) {
    try {
      const track = this.getTrack(id);
      this.removeTrack(track);
    } catch (e) {
      // ATM do nothing
    }
  }

  private removeTrack(track: Track) {
    this.clearTrack(track, false);
    if (track.markerListener) {
      track.markerListener.remove();
    }
    this.tracks.delete(track.id);
  }

  setTrackVisible(id: number, visible?: boolean) {
    const track = this.getTrack(id);
    if (visible != undefined) {
      track.options.visible = visible;
    }
    for (const path of track.paths) {
      path.setVisible(track.options.visible && this.allTracksVisible);
    }
  }

  setTrackMarkerVisible(id: number, visible?: boolean) {
    const track = this.getTrack(id);
    if (visible != undefined) {
      track.options.markerVisible = visible;
    }
    if (track.marker) {
      if (track.options.markerVisible && this.allTracksVisible) {
        track.marker.map = this.map;
      } else {
        track.marker.map = null;
      }
    }
  }

  setTrackDirectionsVisible(id: number, visible?: boolean) {
    const track = this.getTrack(id);
    if (visible != undefined) {
      track.options.directionsVisible = visible;
    }
    if (track.options.directionsVisible && this.allTracksVisible) {
      const trackDirectionIcon: google.maps.IconSequence[] = [this.defaultTrackArrowIcon];
      for (const path of track.paths) {
        path.setOptions({ icons: trackDirectionIcon });
      }
    } else {
      for (const path of track.paths) {
        path.setOptions({ icons: null });
      }
    }
  }

  setTrackClickable(id: number, clickable: boolean) {
    const track = this.getTrack(id);
    track.options.clickable = clickable;
    track.activated = false;
  }

  setTrackHoverable(id: number, hoverable: boolean) {
    const track = this.getTrack(id);
    track.options.hoverable = hoverable;
    track.activated = false;
  }

  private getGeometryId(geometry: GeometryDto, useBaseItemId = true): string {
    let idPrefix = 'bas_';
    if (geometry.field) {
      idPrefix = 'fie_';
    } else if (geometry.location) {
      idPrefix = 'loc_';
    }
    let id = geometry.id;
    if (useBaseItemId) {
      if (geometry.field) {
        id = geometry.field.id;
      } else if (geometry.location) {
        id = geometry.location.id;
      }
    }
    if (id == undefined) {
      this.lastGeometryBasicId--;
      id = this.lastGeometryBasicId;
    }
    return idPrefix + id.toString();
  }

  addGeometryEnhanced(
    item: FieldV2Dto | LocationV2Dto,
    visible = true,
    fill = ExaNewMapComponent.GEOMETRY_FILL_DEFAULT,
    stroke = ExaNewMapComponent.GEOMETRY_STROKE_DEFAULT
  ): string | undefined {
    if (item.geometry) {
      let name;
      let icon = undefined;
      let crop = undefined;
      if (!ExaNewMapComponent.isFieldGeometry(item)) {
        icon = this.getLocationIconName(item);
        name = this.getLocationName(item);
        crop = (item as FieldV2Dto).cropType;
      } else {
        name = this.getFieldName(item);
      }
      const color = this.getColor(item);
      return this.addGeometry(item.geometry, name, item.identifier, item.customer, crop, color, fill, stroke, icon, visible);
    }
    return undefined;
  }

  addArea(
    area: BoundaryDto[],
    color = ExaNewMapComponent.DEFAULT_COLOR,
    fill = ExaNewMapComponent.GEOMETRY_FILL_DEFAULT,
    stroke = ExaNewMapComponent.GEOMETRY_STROKE_DEFAULT,
    visible = true
  ): string | undefined {
    const geometry: GeometryDto = { boundary: area };
    return this.addGeometry(geometry, undefined, undefined, undefined, undefined, color, fill, stroke, undefined, visible);
  }

  addGeometry(
    geometry: GeometryDto,
    name?: string,
    identifier?: string,
    customer?: EnterpriseCustomerIdDto,
    crop?: string,
    color = ExaNewMapComponent.DEFAULT_COLOR,
    fill = ExaNewMapComponent.GEOMETRY_FILL_DEFAULT,
    stroke = ExaNewMapComponent.GEOMETRY_STROKE_DEFAULT,
    icon = 'space_dashboard',
    visible = true
  ): string {
    this.checkForMap();
    const id = this.getGeometryId(geometry);
    if (this.geometries.has(id)) {
      throw new Error('geometry already exists: ' + id);
    }

    visible = visible && this.allGeometriesVisible;

    const poisVisible = this._options?.showGeometryPOIsOverride ?? false;

    if (fill > 1) {
      fill = 1;
    } else if (fill < 0) {
      fill = 0;
    }

    if (stroke > 1) {
      stroke = 1;
    } else if (stroke < 0) {
      stroke = 0;
    }

    const markerListeners: google.maps.MapsEventListener[] = [];
    let centroidMarker: google.maps.marker.AdvancedMarkerElement | undefined = undefined;
    if (icon) {
      let position;
      if (
        !geometry.centroid?.latitude ||
        !geometry.centroid?.longitude ||
        isNaN(geometry.centroid?.latitude) ||
        isNaN(geometry.centroid?.longitude)
      ) {
        // use first boundary coordinate as a workaround
        position = { lat: geometry.boundary![0].boundary![0].latitude ?? 0, lng: geometry.boundary![0].boundary![0].longitude ?? 0 };
      } else {
        position = { lat: geometry.centroid?.latitude ?? 0, lng: geometry.centroid?.longitude ?? 0 };
      }
      const cenMaOpts: google.maps.marker.AdvancedMarkerElementOptions = {
        map: visible && this.showMarkerForZoom(this.currentZoom) ? this.map : null,
        position: position,
        content: this.createCentroidMarker(id, name, geometry.area, identifier, customer, crop, color, icon),
        zIndex: this.geometryZIndex + 2,
      };
      centroidMarker = new google.maps.marker.AdvancedMarkerElement(cenMaOpts);
      // centroid marker clicks are equal to polygon clicks
      const listener: google.maps.MapsEventListener = centroidMarker.addListener('click', () => {
        this.geometryCentroidClickHandler(id);
      });
      markerListeners.push(listener);
    }

    const boundaries: google.maps.Polygon[] = [];
    const boundaryListeners: google.maps.MapsEventListener[] = [];

    if (geometry.boundary) {
      const boundariesConverted = HelperFunctions.boundaryToLatLngLiteral(geometry.boundary);
      for (const boundary_ of boundariesConverted) {
        const polyOpts: google.maps.PolygonOptions = {
          clickable: true,
          draggable: false,
          editable: false,
          geodesic: false,
          map: this.map,
          paths: boundary_,
          strokeColor: color,
          fillColor: color,
          strokeOpacity: stroke,
          fillOpacity: fill,
          visible: visible,
          zIndex: this.geometryZIndex,
        };
        const boundary: google.maps.Polygon = new google.maps.Polygon(polyOpts);
        boundaries.push(boundary);

        const listener: google.maps.MapsEventListener = boundary.addListener('click', () => {
          this.geometryClickHandler(id);
        });
        boundaryListeners.push(listener);
      }
    }

    const pois: POI[] = [];
    if (geometry.pois) {
      for (const poi of geometry.pois) {
        const poiMaOpts: google.maps.marker.AdvancedMarkerElementOptions = {
          map: visible && this.showMarkerForZoom(this.currentZoom) && poisVisible ? this.map : null, // only show on click or if enabled
          position: { lat: poi.coordinate?.latitude ?? 0, lng: poi.coordinate?.longitude ?? 0 },
          content: this.createPOIMarker(poi),
          zIndex: this.geometryZIndex + 1,
        };
        const poiMarker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement(poiMaOpts);
        const poiId = this.generateUniquePOIId();
        pois.push({ id: poiId, marker: poiMarker, baseItem: poi, activated: false });

        const listener: google.maps.MapsEventListener = poiMarker.addListener('click', () => {
          this.poiMarkerClickHandler(id, poiId);
        });
        markerListeners.push(listener);
      }
    }

    const geo: Geometry = {
      id: id,
      activated: false,
      visible: visible,
      fillOpacity: fill,
      strokeOpacity: stroke,
      color: color,
      centroidMarker: centroidMarker,
      centroidMarkerVisible: visible && this.showMarkerForZoom(this.currentZoom),
      boundaries: boundaries,
      pois: pois,
      poisVisible: poisVisible, // only show on click or when its enabled,
      clickable: true,
      boundaryListeners: boundaryListeners,
      markerListeners: markerListeners,
      baseItem: geometry,
    };
    this.geometries.set(id, geo);

    return id;
  }

  updateGeometryEnhanced(item: FieldV2Dto | LocationV2Dto): string | undefined {
    if (item.geometry) {
      let name;
      let icon = undefined;
      let crop = undefined;
      if (!ExaNewMapComponent.isFieldGeometry(item)) {
        icon = this.getLocationIconName(item);
        name = this.getLocationName(item);
        crop = (item as FieldV2Dto).cropType;
      } else {
        name = this.getFieldName(item);
      }
      const color = this.getColor(item);
      return this.updateGeometry(item.geometry, name, color, item.identifier, item.customer, crop, icon);
    }
    return undefined;
  }

  updateGeometry(
    geometry: GeometryDto,
    name?: string,
    color?: string,
    identifier?: string,
    customer?: EnterpriseCustomerIdDto,
    crop?: string,
    icon?: string
  ): string {
    this.checkForMap();
    const id = this.getGeometryId(geometry);
    let visible = true;
    let fillOpacity = undefined;
    let strokeOpacity = undefined;
    try {
      // try to delete the old geometry if it is available
      const geo = this.getGeometry(id);
      fillOpacity = geo.fillOpacity;
      strokeOpacity = geo.strokeOpacity;
      color = geo.color;
      visible = geo.visible;
      this.removeGeometry(geo);
    } catch (e) {
      // ignore
    }
    return this.addGeometry(geometry, name, identifier, customer, crop, color, fillOpacity, strokeOpacity, icon, visible);
  }

  removeGeometries() {
    for (const id of this.geometries.keys()) {
      this.removeGeometryById(id);
    }
  }

  removeGeometryById(id: string) {
    try {
      const geometry = this.getGeometry(id);
      this.removeGeometry(geometry);
    } catch (e) {
      // ATM do nothing
    }
  }

  private removeGeometry(geometry: Geometry) {
    // remove listeners
    for (const listener of geometry.boundaryListeners) {
      listener.remove();
    }
    for (const listener of geometry.markerListeners) {
      listener.remove();
    }
    if (geometry.centroidMarker) {
      geometry.centroidMarker.map = null;
    }
    for (const boundary of geometry.boundaries) {
      boundary.setMap(null);
    }
    for (const poi of geometry.pois) {
      poi.marker.map = null;
    }
    this.geometries.delete(geometry.id);
  }

  setGeometryFillOpacity(id: string, opacity = ExaNewMapComponent.GEOMETRY_FILL_DEFAULT) {
    const geometry = this.getGeometry(id);
    this._setGeometryFillOpacity(geometry, opacity);
  }

  private _setGeometryFillOpacity(geometry: Geometry, opacity = ExaNewMapComponent.GEOMETRY_FILL_DEFAULT) {
    if (opacity > 1) {
      opacity = 1;
    } else if (opacity < 0) {
      opacity = 0;
    }
    for (const boundary of geometry.boundaries) {
      boundary.setOptions({ fillOpacity: opacity });
    }
    geometry.fillOpacity = opacity;
  }

  setGeometryStrokeOpacity(id: string, opacity = ExaNewMapComponent.GEOMETRY_STROKE_DEFAULT) {
    const geometry = this.getGeometry(id);
    this._setGeometryStrokeOpacity(geometry, opacity);
  }

  private _setGeometryStrokeOpacity(geometry: Geometry, opacity = ExaNewMapComponent.GEOMETRY_STROKE_DEFAULT) {
    if (opacity > 1) {
      opacity = 1;
    } else if (opacity < 0) {
      opacity = 0;
    }
    for (const boundary of geometry.boundaries) {
      boundary.setOptions({ strokeOpacity: opacity });
    }
    geometry.strokeOpacity = opacity;
  }

  setGeometryColor(id: string, color = ExaNewMapComponent.DEFAULT_COLOR) {
    const geometry = this.getGeometry(id);
    this._setGeometryColor(geometry, color);
  }

  private _setGeometryColor(geometry: Geometry, color = ExaNewMapComponent.DEFAULT_COLOR) {
    for (const boundary of geometry.boundaries) {
      boundary.setOptions({ fillColor: color, strokeColor: color });
    }
    geometry.color = color;
  }

  setGeometryVisible(id: string, visible?: boolean) {
    const geometry = this.getGeometry(id);
    this._setGeometryVisible(geometry, visible);
  }

  private _setGeometryVisible(geometry: Geometry, visible?: boolean) {
    // deactivate the geometry first if it is active
    if (visible != undefined) {
      geometry.visible = visible;
    }
    if (!geometry.visible && this.allGeometriesVisible && this.currentlyActiveGeometry && this.currentlyActiveGeometry.id == geometry.id) {
      this._setGeometryActive(geometry, false);    }
    for (const boundary of geometry.boundaries) {
      boundary.setVisible(geometry.visible && this.allGeometriesVisible);
    }
  }

  setGeometryCentroidVisible(id: string, visible?: boolean) {
    const geometry = this.getGeometry(id);
    this._setGeometryCentroidVisible(geometry, visible);
  }

  private _setGeometryCentroidVisible(geometry: Geometry, visible?: boolean) {
    if (visible != undefined) {
      geometry.centroidMarkerVisible = visible;
    }
    // - do not change the markers itself if the geometry is active
    // - only show when the zoom is correct
    if (!geometry.activated && geometry.centroidMarker) {
      if (geometry.visible && geometry.centroidMarkerVisible && this.allGeometriesVisible && this.showMarkerForZoom(this.currentZoom)) {
        geometry.centroidMarker.map = this.map;
      } else {
        geometry.centroidMarker.map = null;
      }
    }
  }

  setGeometryPOIsVisible(id: string, visible?: boolean) {
    const geometry = this.getGeometry(id);
    this._setGeometryPOIsVisible(geometry, visible);
  }

  private _setGeometryPOIsVisible(geometry: Geometry, visible?: boolean, force?: boolean) {
    if (visible != undefined) {
      geometry.poisVisible = visible;
    }
    // do not change the markers itself if the geometry is active or POIs are enabled by override
    if (!geometry.activated || force || this.showGeometryPOIsOverrideEnabled()) {
      let map = null;
      if (geometry.visible && geometry.poisVisible && this.allGeometriesVisible && this.showMarkerForZoom(this.currentZoom)) {
        map = this.map;
      }
      for (const poi of geometry.pois) {
        poi.marker.map = map;
      }
    }
  }

  setGeometryCentroidsVisible(visible?: boolean) {
    for (const geometry of this.geometries.values()) {
      this._setGeometryCentroidVisible(geometry, visible);
    }
  }

  setGeometriesPOIsVisible(visible?: boolean) {
    for (const geometry of this.geometries.values()) {
      this._setGeometryPOIsVisible(geometry, visible && (geometry.poisVisible || this.showGeometryPOIsOverrideEnabled()));
    }
  }

    setGeometryActive(id: string, active?: boolean) {
    const geometry = this.getGeometry(id);

    this._setGeometryActive(geometry, active ?? false);
  }



  addNewGeometry(isField: boolean) {
    if (this.manipulatingGeometry) {
      throw new Error('already manipulating another geometry');
    }
    this.manipulatingGeometry = true;
    this.setGeometryFinishButtonDisabled(true);

    this.editingGeometryId = undefined;
    this.editingGeometryIsField = isField;
    this.editingGeometryBoundaries = [];
    this.editingGeometryBoundariesListener = [];
    this.editingGeometryPOIs = [];
    this.editingGeometryPOIsListener = [];

    //this.updateDeleteMarkerOnChange(); -> does not do anything here ;)
    this.setGeometryEditButtonVisible(false);
    this.setGeometryEditButtonsVisible(true);

    // start directly with adding a sub geometry -> faster/better for the user i think
    this.addSubGeometry();
  }

  editGeometry(id: string) {
    if (this.manipulatingGeometry) {
      throw new Error('already manipulating another geometry');
    }
    this.geometryEditStatusChanged.emit({ id: id, status: GeometryEditStatus.INIT });
    const geometry = this.getGeometry(id);
    this._setGeometryActive(geometry, false);    
    this.manipulatingGeometry = true;
    this.setGeometryFinishButtonDisabled(true);
    this.editingGeometryId = id;
    this.editingGeometryIsField = ExaNewMapComponent.isFieldGeometry(geometry.baseItem);

    const color = this.getColorGeometry(geometry.baseItem, ExaNewMapComponent.DEFAULT_COLOR);

    // create duplicate of polygons and hide the original one
    this.editingGeometryBoundaries = [];
    this.editingGeometryBoundariesListener = [];
    for (const boundary_ of geometry.boundaries) {
      const polyOpts: google.maps.PolygonOptions = {
        clickable: false,
        draggable: false,
        editable: true,
        geodesic: false,
        map: this.map,
        paths: this.copyPaths(boundary_.getPaths()),
        strokeColor: color,
        fillColor: color,
        strokeOpacity: 0.8,
        fillOpacity: ExaNewMapComponent.GEOMETRY_FILL_DEFAULT,
        visible: true,
        zIndex: this.geometryZIndex,
      };
      const boundary: google.maps.Polygon = new google.maps.Polygon(polyOpts);
      this.editingGeometryBoundaries.push(boundary);

      // add listener for every change in the geometry in the map
      let listener: google.maps.MapsEventListener = boundary.getPaths().addListener('set_at', () => {
        this.updateDeleteMarkerOnChange();
      });
      this.editingGeometryBoundariesListener.push(listener);
      listener = boundary.getPaths().addListener('insert_at', () => {
        this.updateDeleteMarkerOnChange();
      });
      this.editingGeometryBoundariesListener.push(listener);
      listener = boundary.getPaths().addListener('remove_at', () => {
        this.updateDeleteMarkerOnChange();
      });
      this.editingGeometryBoundariesListener.push(listener);
      for (const path of boundary.getPaths().getArray()) {
        listener = path.addListener('set_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener.push(listener);
        listener = path.addListener('insert_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener.push(listener);
        listener = path.addListener('remove_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener.push(listener);
      }
    }

    this.editingGeometryPOIs = [];
    this.editingGeometryPOIsListener = [];
    if (geometry.pois) {
      for (const poi of geometry.pois) {
        const poiMaOpts: google.maps.marker.AdvancedMarkerElementOptions = {
          map: this.map,
          gmpDraggable: true,
          position: { lat: poi.baseItem.coordinate?.latitude ?? 0, lng: poi.baseItem.coordinate?.longitude ?? 0 },
          content: this.createPOIMarker(poi.baseItem),
          zIndex: this.geometryZIndex + 1,
        };
        const poiMarker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement(poiMaOpts);
        this.editingGeometryPOIs.push({ id: poi.id, marker: poiMarker, baseItem: poi.baseItem, activated: false });
        const listener: google.maps.MapsEventListener = poiMarker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          this.updatePOIPosition(poi.id, event.latLng);
        });
        this.editingGeometryPOIsListener.push(listener);
      }
    }

    this.updateDeleteMarkerOnChange(true);
    this.setGeometryEditButtonVisible(false);
    this.setGeometryEditButtonsVisible(true);
    this._setGeometryVisible(geometry, false);
    this._setGeometryCentroidVisible(geometry, false);
    this._setGeometryPOIsVisible(geometry, false);

    this.geometryEditStatusChanged.emit({ id: id, status: GeometryEditStatus.START });

  }

  private copyPaths(
    paths: google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>
  ): google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>> {
    const paths_cpy: google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>> = new google.maps.MVCArray<
      google.maps.MVCArray<google.maps.LatLng>
    >();
    for (const path of paths.getArray()) {
      const path_cpy: google.maps.MVCArray<google.maps.LatLng> = new google.maps.MVCArray<google.maps.LatLng>();
      for (const point of path.getArray()) {
        path_cpy.push(new google.maps.LatLng(point));
      }
      paths_cpy.push(path_cpy);
    }
    return paths_cpy;
  }

  private setGeometryAddButtonVisible(visible: boolean) {
    const startButton = document.getElementById('geometry-add-start-' + this.id.toString());
    if (visible) {
      startButton?.classList.add('visible');
    } else {
      startButton?.classList.remove('visible');
    }
  }

  private setGeometryEditButtonVisible(visible: boolean) {
    const startButton = document.getElementById('geometry-edit-start-' + this.id.toString());
    if (visible) {
      startButton?.classList.add('visible');
    } else {
      startButton?.classList.remove('visible');
    }
  }

  private setGeometryEditButtonsVisible(visible: boolean) {
    this.setGeometryAddButtonVisible(!visible);
    const finishButton = document.getElementById('geometry-edit-finish-' + this.id.toString());
    const cancelButton = document.getElementById('geometry-edit-cancel-' + this.id.toString());
    const addSubGeoButton = document.getElementById('geometry-edit-add-sub-geometry-' + this.id.toString());
    const addPOIButton = document.getElementById('geometry-edit-add-poi-' + this.id.toString());
    if (visible) {
      finishButton?.classList.add('visible');
      cancelButton?.classList.add('visible');
      addSubGeoButton?.classList.add('visible');
      addPOIButton?.classList.add('visible');
    } else {
      finishButton?.classList.remove('visible');
      cancelButton?.classList.remove('visible');
      addSubGeoButton?.classList.remove('visible');
      addPOIButton?.classList.remove('visible');
    }
  }

  private setGeometryAddButtonDisabled(disabled: boolean) {
    const button = document.getElementById('geometry-add-start-' + this.id.toString());
    if (disabled) {
      button?.classList.add(this.buttonDisabledClass);
    } else {
      button?.classList.remove(this.buttonDisabledClass);
    }
  }

  private setGeometryEditButtonDisabled(disabled: boolean) {
    const button = document.getElementById('geometry-edit-start-' + this.id.toString());
    if (disabled) {
      button?.classList.add(this.buttonDisabledClass);
    } else {
      button?.classList.remove(this.buttonDisabledClass);
    }
  }

  private setGeometryEditAddSubGeoButtonDisabled(disabled: boolean) {
    const button = document.getElementById('geometry-edit-add-sub-geometry-' + this.id.toString());
    if (disabled) {
      button?.classList.add(this.buttonDisabledClass);
    } else {
      button?.classList.remove(this.buttonDisabledClass);
    }
  }

  private setGeometryEditAddPOIButtonDisabled(disabled: boolean) {
    const button = document.getElementById('geometry-edit-add-poi-' + this.id.toString());
    if (disabled) {
      button?.classList.add(this.buttonDisabledClass);
    } else {
      button?.classList.remove(this.buttonDisabledClass);
    }
  }

  private setGeometryFinishButtonDisabled(disabled: boolean) {    
    const button = document.getElementById('geometry-edit-finish-' + this.id.toString());
    if (disabled) {
      button?.classList.add(this.buttonDisabledClass);
    } else {
      button?.classList.remove(this.buttonDisabledClass);
    }
  }

  toggleShowRoads() {
    if (this.mapTypeIsHybrid()) {
      this.map?.setMapTypeId(google.maps.MapTypeId.ROADMAP);
    } else {
      this.map?.setMapTypeId(google.maps.MapTypeId.HYBRID);
    }
  }

  toggleShowAllTracks() {
    this.allTracksVisible = !this.allTracksVisible;
    for (const id of this.tracks.keys()) {
      this.setTrackVisible(id);
      this.setTrackMarkerVisible(id);
    }
  }

  toggleShowAllGeometries() {
    this.allGeometriesVisible = !this.allGeometriesVisible;
    for (const id of this.geometries.keys()) {
      this.setGeometryVisible(id);
      this.setGeometryCentroidVisible(id);
    }
  }

  toggleShowAllOverlays() {
    this.allOverlaysVisible = !this.allOverlaysVisible;
    for (const id of this.svgOverlays.keys()) {
      this.setOverlayVisible(id);
    }
  }

  toggleShowAllCustomMarker() {
    this.allCustomMarkerVisible = !this.allCustomMarkerVisible;
    for (const id of this.customMarker.keys()) {
      this.setCustomMarkerVisible(id);
    }
  }

  startGeometryAdd(isField: boolean) {
    // if (overlayPanel) {
    //   overlayPanel.toggle({});
    // }
    if (this.currentlyActiveGeometry) {
      this._setGeometryActive(this.currentlyActiveGeometry, false);    }
    this.addNewGeometry(isField);
  }

  startCurrentGeometryEdit() {
    if (this.currentlyActiveGeometry) {      
      this.editGeometry(this.currentlyActiveGeometry.id);
    } else {
      throw new Error('no geometry active');
    }
  }

  addSubGeometry() {
    if (!this.manipulatingGeometry) {
      throw new Error('no geometry adding/editing active');
    }
    this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POLYGON_ADD_START });
    // disable addSubGeo and addPoi button
    this.setGeometryEditAddSubGeoButtonDisabled(true);
    this.setGeometryEditAddPOIButtonDisabled(true);
    this.setGeometryFinishButtonDisabled(true);

    let color = ExaNewMapComponent.DEFAULT_COLOR;
    if (this.editingGeometryId) {
      const geometry = this.getGeometry(this.editingGeometryId);
      color = this.getColorGeometry(geometry.baseItem, ExaNewMapComponent.DEFAULT_COLOR);
    }

    // change cursor
    this.map?.setOptions({ draggableCursor: 'crosshair' });
    // first create a new polyline until we have a connected polygon
    // done by clicking on the first marker of the polyline and having at least 3 marker
    const polyOpts: google.maps.PolylineOptions = {
      clickable: false,
      draggable: false,
      editable: true,
      geodesic: false,
      map: this.map,
      path: [],
      strokeColor: color,
      strokeOpacity: 1,
      //strokeWeight: track.options.strokeWeight,
      visible: true,
      zIndex: this.foregroundZIndex,
    };
    this.unfinishedPolygon = new google.maps.Polyline(polyOpts);
    this.unfinishedPolygonMarker = [];
    this.unfinishedPolygonMarkerListener = [];
    this.unfinishedPolygonListener = [];

    // add listener for every change in the geometry in the map
    let listener: google.maps.MapsEventListener = this.unfinishedPolygon.getPath().addListener('set_at', () => {
      this.updateUnfinishedPolygonMarkerOnChange();
    });
    this.unfinishedPolygonListener.push(listener);
    listener = this.unfinishedPolygon.getPath().addListener('insert_at', () => {
      this.updateUnfinishedPolygonMarkerOnChange();
    });
    this.unfinishedPolygonListener.push(listener);
    listener = this.unfinishedPolygon.getPath().addListener('remove_at', () => {
      this.updateUnfinishedPolygonMarkerOnChange();
    });
    this.unfinishedPolygonListener.push(listener);
  }

  addMarkerToUnfinishedPolygon(position: google.maps.LatLng) {
    if (!this.unfinishedPolygon) {
      throw new Error('no unfinished polygon avaiable');
    }
    if (!position) {
      return;
    }
    // add the marker to the polyline
    this.unfinishedPolygon.getPath().push(position);
    // handling of updating markers and so on is done through polygon listener
  }

  private updateUnfinishedPolygonMarkerOnChange() {
    // remove previous marker if available
    if (this.unfinishedPolygonMarkerListener) {
      for (const markerListener of this.unfinishedPolygonMarkerListener) {
        markerListener.remove();
      }
    }
    if (this.unfinishedPolygonMarker) {
      for (const marker of this.unfinishedPolygonMarker) {
        marker.map = null;
      }
    }
    this.unfinishedPolygonMarker = [];
    this.unfinishedPolygonMarkerListener = [];
    // also create a delete marker and add a listener
    if (this.unfinishedPolygon) {
      for (let pointId = 0; pointId < this.unfinishedPolygon.getPath().getLength(); pointId++) {
        const point = this.unfinishedPolygon.getPath().getArray()[pointId];
        if (pointId == 0) {
          // add a listener to the marker and show a special "start" marker
          const maOpts: google.maps.marker.AdvancedMarkerElementOptions = {
            map: this.map,
            zIndex: this.foregroundZIndex + 1,
            position: point,
            //content: markerContent, -> currently empty ;) -> default marker
          };
          const marker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement(maOpts);
          this.unfinishedPolygonMarker?.push(marker);
          const listener: google.maps.MapsEventListener = marker.addListener('click', (_event: google.maps.MapMouseEvent) => {
            this.finishPolygon();
          });
          this.unfinishedPolygonMarkerListener.push(listener);
        } else {
          const delMaOpts: google.maps.marker.AdvancedMarkerElementOptions = {
            map: this.map,
            position: { lat: point.lat(), lng: point.lng() },
            content: this.createDeleteMarker(),
            zIndex: this.geometryZIndex + 1,
          };
          const delMarker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement(delMaOpts);
          this.unfinishedPolygonMarker.push(delMarker);
          const listener: google.maps.MapsEventListener = delMarker.addListener('click', () => {
            this.deleteMarkerUnfinishedPolygonClickHandler(pointId);
          });
          this.unfinishedPolygonMarkerListener.push(listener);
        }
      }
    }
  }

  private deleteMarkerUnfinishedPolygonClickHandler(pointId: number) {
    if (this.unfinishedPolygon) {
      if (pointId < this.unfinishedPolygon.getPath().getLength()) {
        if (this.unfinishedPolygon.getPath().getLength() < 2) {
          console.warn('last point can not be removed');
          return;
        } else {
          // only remove point from path
          this.unfinishedPolygon.getPath().removeAt(pointId);
        }
      }
      // rebuild of everything is handled by path listeners
    }
  }

  finishPolygon() {
    if (!this.unfinishedPolygon) {
      throw new Error('no unfinished polygon avaiable');
    }
    if (this.unfinishedPolygon.getPath().getLength() >= 3) {
      if (!this.editingGeometryBoundaries) {
        this.editingGeometryBoundaries = [];
      }
      // create a new polygon and add it to the current editing geometry object
      // check if starting point is inside any of the boundary polygons
      const testPoint: google.maps.LatLng = this.unfinishedPolygon.getPath().getAt(0);
      let boundary: google.maps.Polygon | null = null;
      for (const polygon of this.editingGeometryBoundaries) {
        if (google.maps.geometry.poly.containsLocation(testPoint, polygon)) {
          boundary = polygon;
          break;
        }
      }

      let color = ExaNewMapComponent.DEFAULT_COLOR;
      if (this.editingGeometryId) {
        const geometry = this.getGeometry(this.editingGeometryId);
        color = this.getColorGeometry(geometry.baseItem, ExaNewMapComponent.DEFAULT_COLOR);
      }

      if (!boundary) {
        // just create a new polygon since it can not be any hole -> no polygon match
        const paths = new google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>();
        paths.push(this.unfinishedPolygon.getPath());
        const polyOpts: google.maps.PolygonOptions = {
          clickable: false,
          draggable: false,
          editable: true,
          geodesic: false,
          map: this.map,
          paths: paths,
          strokeColor: color,
          fillColor: color,
          strokeOpacity: 0.8,
          fillOpacity: ExaNewMapComponent.GEOMETRY_FILL_DEFAULT,
          visible: true,
          zIndex: this.geometryZIndex,
        };
        boundary = new google.maps.Polygon(polyOpts);
        this.editingGeometryBoundaries.push(boundary);

        // add listener for every change in the geometry in the map
        let listener: google.maps.MapsEventListener = boundary.getPaths().addListener('set_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener?.push(listener);
        listener = boundary.getPaths().addListener('insert_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener?.push(listener);
        listener = boundary.getPaths().addListener('remove_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener?.push(listener);
        for (const path of boundary.getPaths().getArray()) {
          listener = path.addListener('set_at', () => {
            this.updateDeleteMarkerOnChange();
          });
          this.editingGeometryBoundariesListener?.push(listener);
          listener = path.addListener('insert_at', () => {
            this.updateDeleteMarkerOnChange();
          });
          this.editingGeometryBoundariesListener?.push(listener);
          listener = path.addListener('remove_at', () => {
            this.updateDeleteMarkerOnChange();
          });
          this.editingGeometryBoundariesListener?.push(listener);
        }
        this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POLYGON_ADD_FINISH });
      } else {
        // the new geometry is a hole inside an existing polygon
        // check if we have to reverse the new polygon
        const polygonClockwise = HelperFunctions.isClockwisePath_(boundary.getPaths().getAt(0));
        const newPolygonClockwise = HelperFunctions.isClockwisePath_(this.unfinishedPolygon.getPath());
        const newPath: google.maps.LatLng[] = this.unfinishedPolygon.getPath().getArray();
        if (polygonClockwise == newPolygonClockwise) {
          // reverse the new polygon
          newPath.reverse();
        }
        const path = new google.maps.MVCArray<google.maps.LatLng>();
        for (const point of newPath) {
          path.push(point);
        }
        let listener: google.maps.MapsEventListener = path.addListener('set_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener?.push(listener);
        listener = path.addListener('insert_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener?.push(listener);
        listener = path.addListener('remove_at', () => {
          this.updateDeleteMarkerOnChange();
        });
        this.editingGeometryBoundariesListener?.push(listener);
        boundary.getPaths().push(path);
        this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POLYGON_ADD_FINISH });
      }
      this.resetUnfinishedPolygon(false);
      // update the delete marker set
      this.updateDeleteMarkerOnChange();
    }
  }

  finishGeometryEdit() {
    if (this.manipulatingGeometry) {
      // check if at least one boundary is available
      if (this.editingGeometryBoundaries && this.editingGeometryBoundaries.length > 0) {
        const geometry: GeometryDto = this.createGeometryFromEditingData();

        this.geometryChanged.emit({ id: this.editingGeometryId ?? '', geometry: geometry, isField: this.editingGeometryIsField ?? true });
        this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.FINISH });        // call cancel here since it is only doing some cleanup ;)
        this.cancelButton(false);
      }
    } else {
      throw new Error('no geometry edit active');
    }
  }

  addPOI() {
    if (!this.manipulatingGeometry) {
      throw new Error('no geometry adding/editing active');
    }
    this.showPOIEditDialog();

    this.addingPOI = true;
    // create a default POI
    this.addingPOIData = { type: MarkerTypeEnum.INFO, comment: 'default marker' };

    // disable addSubGeo and addPoi button
    this.setGeometryEditAddSubGeoButtonDisabled(true);
    this.setGeometryEditAddPOIButtonDisabled(true);
    this.setGeometryFinishButtonDisabled(true);
    this.setPOIsDraggable(false);
    // change cursor
    this.map?.setOptions({ draggableCursor: 'crosshair' });
    this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POI_ADD_START });
  }

  private setPOIsDraggable(draggable: boolean) {
    if (this.editingGeometryPOIs) {
      for (const poi of this.editingGeometryPOIs) {
        poi.marker.gmpDraggable = draggable;
      }
    }
  }

  addPOIMarker(position: google.maps.LatLng) {
    // clicking on a map finalizes a POI
    if (!this.addingPOI) {
      throw new Error('no POI adding active');
    }
    if (!this.addingPOIData) {
      return;
    }
    if (!position) {
      return;
    }
    this.map?.setOptions({ draggableCursor: 'default' });

    // update POI coordinate with the selected position
    this.addingPOIData.coordinate = { latitude: position.lat(), longitude: position.lng() };

    // create new POI marker
    const poiMaOpts: google.maps.marker.AdvancedMarkerElementOptions = {
      map: this.map,
      gmpDraggable: true,
      position: position,
      content: this.createPOIMarker(this.addingPOIData),
      zIndex: this.geometryZIndex + 1,
    };
    const poiMarker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement(poiMaOpts);

    const poiId = this.generateUniquePOIId();
    const listener: google.maps.MapsEventListener = poiMarker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
      this.updatePOIPosition(poiId, event.latLng);
    });
    this.editingGeometryPOIsListener?.push(listener);

    this.editingGeometryPOIs?.push({ id: poiId, marker: poiMarker, baseItem: this.addingPOIData, activated: false });
    this.updateDeleteMarkerOnChange();

     this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POI_ADD_FINISH });
    this.resetAddPOI(false);
  }

  protected showPOIEditDialog() {
    // has to be implemented in extending class
    throw new Error('not implemented');
  }

  /*
   * Used in conjunction with 'showPOIEditDialog' to feed back data of the newly created POI
   */
  addPOIData(poi: POIDto) {
    if (!this.addingPOI) {
      throw new Error('no POI adding active');
    }
    if (!poi) {
      this.resetAddPOI();
      return;
    }
    if (!poi.type) {
      throw new Error('no POI type set');
    }
    this.addingPOIData = poi;
  }

  cancelButton(emitStatusEvent = true) {
    if (this.unfinishedPolygon) {
      if (!this.editingGeometryBoundaries || this.editingGeometryBoundaries.length == 0) {
        this.setGeometryEditButtonsVisible(false);
        // do some cleanup
        this.resetGeometryEditHelper();
        this.manipulatingGeometry = false;
        if (emitStatusEvent) {
          this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.CANCEL });
        }
      } else {
        this.resetUnfinishedPolygon();
      }
    } else if (this.addingPOI) {
      this.resetAddPOI();
    } else if (this.manipulatingGeometry) {
      this.setGeometryEditButtonsVisible(false);
      if (this.editingGeometryId) {
        this.setGeometryVisible(this.editingGeometryId, true);
        if (this.showMarkerForZoom(this.currentZoom)) {
          this.setGeometryCentroidVisible(this.editingGeometryId, true);
        } else {
          this.setGeometryCentroidVisible(this.editingGeometryId, false);
        }
        this.setGeometryPOIsVisible(this.editingGeometryId, false);
      }
      // do some cleanup
      this.resetGeometryEditHelper();
      this.manipulatingGeometry = false;
      if (emitStatusEvent) {
        this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.CANCEL });
      }
    } else {
      throw new Error('no geometry edit active');
    }
  }

  private resetGeometryEditHelper() {
    // do some cleanup
    if (this.editingGeometryDeleteMarkerListener) {
      for (const b of this.editingGeometryDeleteMarkerListener) {
        b.remove();
      }
    }
    if (this.editingGeometryDeleteMarker) {
      for (const b of this.editingGeometryDeleteMarker) {
        b.map = null;
      }
    }
    if (this.editingGeometryBoundariesListener) {
      for (const b of this.editingGeometryBoundariesListener) {
        b.remove();
      }
    }
    if (this.editingGeometryBoundaries) {
      for (const b of this.editingGeometryBoundaries) {
        b.setMap(null);
      }
    }
    if (this.editingGeometryPOIs) {
      for (const b of this.editingGeometryPOIs) {
        b.marker.map = null;
      }
    }
    if (this.editingGeometryPOIsListener) {
      for (const b of this.editingGeometryPOIsListener) {
        b.remove();
      }
    }

    this.resetUnfinishedPolygon(false);
    this.resetAddPOI(false);

    this.editingGeometryId = undefined;
    this.editingGeometryIsField = undefined;
    this.editingGeometryBoundaries = undefined;
    this.editingGeometryBoundariesListener = undefined;
    this.editingGeometryDeleteMarker = undefined;
    this.editingGeometryDeleteMarkerListener = undefined;
    this.editingGeometryPOIs = undefined;
    this.editingGeometryPOIsListener = undefined;
  }

  private resetUnfinishedPolygon(emitStatusEvent = true) {
    if (this.unfinishedPolygon) {
      this.unfinishedPolygon.setMap(null);
    }
    if (this.unfinishedPolygonMarker) {
      for (const marker of this.unfinishedPolygonMarker) {
        marker.map = null;
      }
    }
    if (this.unfinishedPolygonMarker) {
      for (const marker of this.unfinishedPolygonMarker) {
        marker.map = null;
      }
    }
    if (this.unfinishedPolygonMarkerListener) {
      for (const marker of this.unfinishedPolygonMarkerListener) {
        marker.remove();
      }
    }
    if (this.unfinishedPolygonListener) {
      for (const marker of this.unfinishedPolygonListener) {
        marker.remove();
      }
    }
    this.unfinishedPolygon = undefined;
    this.unfinishedPolygonMarker = undefined;
    this.unfinishedPolygonMarkerListener = undefined;
    this.unfinishedPolygonListener = undefined;

    this.setGeometryEditAddSubGeoButtonDisabled(false);
    this.setGeometryEditAddPOIButtonDisabled(false);
    this.map?.setOptions({ draggableCursor: 'default' });
    if (emitStatusEvent) {
      this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POLYGON_ADD_CANCEL });
    }
  }

  private resetAddPOI(emitStatusEvent = true) {
    this.addingPOI = false;
    this.addingPOIData = undefined;

    this.setGeometryEditAddSubGeoButtonDisabled(false);
    this.setGeometryEditAddPOIButtonDisabled(false);
    if (this.editingGeometryBoundaries && this.editingGeometryBoundaries.length > 0) {
      // finish is available -> at least one boundary
      this.setGeometryFinishButtonDisabled(false);
    } else {
      // finish is blocked -> no boundary
      this.setGeometryFinishButtonDisabled(true);
    }
    this.setPOIsDraggable(true);
    this.map?.setOptions({ draggableCursor: 'default' });
    if (emitStatusEvent) {
      this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POI_ADD_CANCEL });
    }
  }

  private updatePOIPosition(id: number, position: google.maps.LatLng | null) {
    const poi = this.getPOIById(this.editingGeometryPOIs ?? [], id);
    if (position && poi?.baseItem.coordinate) {
      // update baseItem position
      poi.baseItem.coordinate.latitude = position.lat();
      poi.baseItem.coordinate.longitude = position.lng();
      this.updateDeleteMarkerOnChange();
    }
  }

  private updateDeleteMarkerOnChange(init?: boolean) {
    // remove previous marker if available
    if (this.editingGeometryDeleteMarkerListener) {
      for (const markerListener of this.editingGeometryDeleteMarkerListener) {
        markerListener.remove();
      }
    }
    if (this.editingGeometryDeleteMarker) {
      for (const marker of this.editingGeometryDeleteMarker) {
        marker.map = null;
      }
    }
    this.editingGeometryDeleteMarker = [];
    this.editingGeometryDeleteMarkerListener = [];
    // also create a delete marker and add a listener
    if (this.editingGeometryBoundaries) {
      for (let boundaryId = 0; boundaryId < this.editingGeometryBoundaries.length; boundaryId++) {
        const boundary = this.editingGeometryBoundaries[boundaryId];
        for (let pathId = 0; pathId < boundary.getPaths().getLength(); pathId++) {
          const path = boundary.getPaths().getArray()[pathId];
          for (let pointId = 0; pointId < path.getLength(); pointId++) {
            const point = path.getArray()[pointId];
            const delMaOpts: google.maps.marker.AdvancedMarkerElementOptions = {
              map: this.map,
              position: { lat: point.lat(), lng: point.lng() },
              content: this.createDeleteMarker(),
              zIndex: this.geometryZIndex + 1,
            };
            const delMarker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement(delMaOpts);
            this.editingGeometryDeleteMarker.push(delMarker);
            const listener: google.maps.MapsEventListener = delMarker.addListener('click', () => {
              this.deleteMarkerClickHandler(boundaryId, pathId, pointId);
            });
            this.editingGeometryDeleteMarkerListener.push(listener);
          }
        }
      }
    }

    if (this.editingGeometryPOIs) {
      for (let poiId = 0; poiId < this.editingGeometryPOIs.length; poiId++) {
        const poi: POI = this.editingGeometryPOIs[poiId];
        const delMaOpts: google.maps.marker.AdvancedMarkerElementOptions = {
          map: this.map,
          position: { lat: poi.baseItem.coordinate?.latitude ?? 0, lng: poi.baseItem.coordinate?.longitude ?? 0 },
          content: this.createDeleteMarker(),
          zIndex: this.geometryZIndex + 1,
        };
        const delMarker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement(delMaOpts);
        this.editingGeometryDeleteMarker.push(delMarker);
        const listener: google.maps.MapsEventListener = delMarker.addListener('click', () => {
          this.deleteMarkerPOIClickHandler(poi.id);
        });
        this.editingGeometryDeleteMarkerListener.push(listener);
      }
    }

    if (!init) {
      if (this.editingGeometryBoundaries && this.editingGeometryBoundaries.length > 0) {
        // finish is available -> at least one boundary
        this.setGeometryFinishButtonDisabled(false);
      } else {
        // finish is blocked -> no boundary
        this.setGeometryFinishButtonDisabled(true);
      }
    }
  }

  private deleteMarkerClickHandler(boundaryId: number, pathId: number, pointId: number) {
    if (this.editingGeometryBoundaries) {
      const boundary = this.editingGeometryBoundaries[boundaryId];
      if (pathId < boundary.getPaths().getLength()) {
        const path = boundary.getPaths().getAt(pathId);
        if (pointId < path.getLength()) {
          // if less than 3 marker remain -> delete the path completely
          if (path.getLength() <= 3) {
            // remove path
            // only delete path if it is not the last path of the whole geometry ;)
            // check if there is a need to delete the parent boundary -> boundary length == 1
            if (boundary.getPaths().getLength() <= 1) {
              // also delete the boundary -> but only if it is not the last boundary
              if (this.editingGeometryBoundaries.length > 1) {
                // remove boundary from geometry
                boundary.setMap(null); // remove polygon also
                this.editingGeometryBoundaries?.splice(boundaryId, 1);
                this.updateDeleteMarkerOnChange();
                this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POLYGON_REMOVE });
              } else {
                console.warn('last path can not be removed');
                return;
              }
            } else {
              // remove path from boundary
              boundary.getPaths().removeAt(pathId);
              this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POLYGON_REMOVE });
            }
          } else {
            // only remove point from path
            path.removeAt(pointId);
            this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POLYGON_REMOVE });
          }
        }
      }
      // rebuild of everything is handled by polygon path listeners
    }
  }

  private deleteMarkerPOIClickHandler(poiId: number) {
    if (this.editingGeometryPOIs) {
      // search for POI with id=poiId
      let del_i = null;
      for (let i = 0; i < this.editingGeometryPOIs.length; i++) {
        if (this.editingGeometryPOIs[i].id == poiId) {
          del_i = i;
          break;
        }
      }

      if (del_i != null) {
        const poi: POI = this.editingGeometryPOIs[del_i];
        poi.marker.map = null;
        this.editingGeometryPOIs?.splice(del_i, 1);
        this.updateDeleteMarkerOnChange();
        this.geometryEditStatusChanged.emit({ id: this.editingGeometryId ?? '', status: GeometryEditStatus.POLYGON_REMOVE });
      }
    }
  }

  setGeometryClickable(id: string, clickable: boolean) {
    const geometry = this.getGeometry(id);
    geometry.clickable = clickable;
    geometry.activated = false;
  }

  private poiMarkerClickHandler(geometryId: string, poiId: number) {
    const geometry = this.getGeometry(geometryId);
    if (geometry && geometry.clickable && !this.manipulatingGeometry) {
      // disable other opened geometry
      if (this.currentlyActiveGeometry && this.currentlyActiveGeometryPOI && this.currentlyActiveGeometryPOI.id != poiId) {
        this.setGeometryPOIActive(this.currentlyActiveGeometry, this.currentlyActiveGeometryPOI, false);
      }
      const poi: POI | undefined = this.getPOIById(geometry.pois, poiId);
      if (poi) {
        this.setGeometryPOIActive(geometry, poi, !poi.activated);
      }
    }
  }

  private trackMarkerClickHandler(id: number) {
    if (this.markerInfoWindowsEnabled()) {
      // if the track marker is active
      if (this.currentlyActiveTrack && this.currentlyActiveTrack.id == id) {
        this.machineClicked.emit({ id: id });
      }
      const track = this.getTrack(id);
      if (track && track.options.clickable && track.options.markerVisible) {
        // disable other opened track
        if (this.currentlyActiveTrack && this.currentlyActiveTrack.id != id) {
          this.setTrackActive(this.currentlyActiveTrack, false);
        }
        this.setTrackActive(track, !track.activated);
      }
    } else {
      this.machineClicked.emit({ id: id });
    }
  }

  public setTrackActiveById(id: number, active: boolean) {
    if (this.markerInfoWindowsEnabled()) {
      const track = this.getTrack(id);
      if (track && track.options.clickable && track.options.markerVisible) {
        if (active) {
          // disable other opened track
          if (this.currentlyActiveTrack && this.currentlyActiveTrack.id != id) {
            this.setTrackActive(this.currentlyActiveTrack, false);
          }
        }
        this.setTrackActive(track, active);
      }
    }
  }

  private setTrackActive(track: Track, active: boolean) {
    track.activated = !track.activated;
    if (track.marker) {
      if (track.activated) {
        track.marker.zIndex = this.foregroundZIndex;
        track.marker.element.firstElementChild?.classList.add('activated');
      } else {
        let zIndex = track.id;
        if (zIndex < 0) {
          zIndex = zIndex * -1;
        }
        track.marker.zIndex = zIndex;
        track.marker.element.firstElementChild?.classList.remove('activated');
      }
    }

    if (active) {
      this.currentlyActiveTrack = track;
    } else {
      this.currentlyActiveTrack = undefined;
    }

    this.machineActiveChanged.emit({ id: track.id, active: track.activated });
  }

  public focusOnTrack(id: number, zoom = 17) {
    const track = this.getTrack(id);
    if (track.marker?.position) {
      this.setCenter(track.marker.position, true);
      this.setZoom(zoom);
    }
  }

  private trackHoverHandler(id: number, event: google.maps.PolyMouseEvent) {
    this.trackHovered.emit({ id: id, position: { lat: event.latLng?.lat() ?? 0, lng: event.latLng?.lng() ?? 0 } });
  }

  private customMarkerClickHandler(id: number) {
    const marker = this.getCustomMarker(id);
    if (marker && marker.clickable) {
      if (this.markerInfoWindowsEnabled()) {
        // disable other opened custom marker
        if (this.currentlyActiveCustomMarker && this.currentlyActiveCustomMarker.id != id) {
          this.setCustomMarkerActive(this.currentlyActiveCustomMarker, false);
        }
        this.setCustomMarkerActive(marker, !marker.activated);
      } else {
        const bi = marker.baseItem;
        this.customMarkerClicked.emit({ id: id, id_orig: bi.id ?? 0, baseItem: bi });
      }
    }
  }

  private setCustomMarkerActive(customMarker: CustomMarker, active: boolean) {
    customMarker.activated = !customMarker.activated;
    if (customMarker.marker) {
      if (customMarker.activated) {
        customMarker.marker.zIndex = this.foregroundZIndex;
        customMarker.marker.element.firstElementChild?.classList.add('activated');
      } else {
        customMarker.marker.zIndex = customMarker.zIndex;
        customMarker.marker.element.firstElementChild?.classList.remove('activated');
      }
    }

    if (active) {
      this.currentlyActiveCustomMarker = customMarker;
    } else {
      this.currentlyActiveCustomMarker = undefined;
    }

    this.customMarkerActiveChanged.emit({ id: customMarker.id, active: customMarker.activated });
  }

  private geometryCentroidClickHandler(id: string) {
    if (this.markerInfoWindowsEnabled()) {
      // if the geometry marker is active
      if (this.currentlyActiveGeometry && this.currentlyActiveGeometry.id == id) {
        this.geometryClicked.emit({
          id: id,
          id_orig: this.currentlyActiveGeometry.baseItem.id ?? 0,
          baseItem: this.currentlyActiveGeometry.baseItem,
        });
      }
      // just call the normal handler
      this.geometryClickHandler(id);
    } else {
      const bi = this.getGeometry(id).baseItem;
      this.geometryClicked.emit({ id: id, id_orig: bi.id ?? 0, baseItem: bi });
    }
  }

  private geometryClickHandler(id: string) {
    const geometry = this.getGeometry(id);
    if (geometry && geometry.clickable && !this.manipulatingGeometry) {
      if (this.markerInfoWindowsEnabled()) {
        // disable other opened geometry
        if (this.currentlyActiveGeometry && this.currentlyActiveGeometry.id != id) {
          this._setGeometryActive(this.currentlyActiveGeometry, false);        }
        this._setGeometryActive(geometry, !geometry.activated);      } else {
        const bi = this.getGeometry(id).baseItem;
        this.geometryClicked.emit({ id: id, id_orig: bi.id ?? 0, baseItem: bi });
      }
    }
  }

   private _setGeometryActive(geometry: Geometry, active: boolean) {
    if (this.currentlyActiveGeometry && this.currentlyActiveGeometry.id != geometry.id) {
      throw new Error('another geometry is currently active');
    }    

    geometry.activated = active;
  
    if (geometry.activated) {
      if (geometry.centroidMarker) {
        if (!geometry.centroidMarkerVisible) {
          // also make it visible -> otherwise no activated marker ;)
          geometry.centroidMarker.map = this.map;
        }
        geometry.centroidMarker.zIndex = this.foregroundZIndex;
        geometry.centroidMarker.element.firstElementChild?.classList.add('activated');
      }
    } else {
      if (geometry.centroidMarker) {
        if (!geometry.centroidMarkerVisible || !this.showMarkerForZoom(this.currentZoom)) {
          // also make it invisible again
          geometry.centroidMarker.map = null;
        }
        geometry.centroidMarker.zIndex = this.geometryZIndex + 2;
        geometry.centroidMarker.element.firstElementChild?.classList.remove('activated');
      }

      // hide any activated POI marker
      if (this.currentlyActiveGeometry && this.currentlyActiveGeometryPOI) {
        this.setGeometryPOIActive(this.currentlyActiveGeometry, this.currentlyActiveGeometryPOI, false);
      }      
    }

    // show all pois for this field
    if (!this._options?.showGeometryPOIsOverride) {
      // only update the POI markers if the override is not set to true
      this._setGeometryPOIsVisible(geometry, geometry.activated, true);
    }

    if (active) {
      this.currentlyActiveGeometry = geometry;
    } else {
      this.currentlyActiveGeometry = undefined;
    }

    if (this._options?.disableGeometryEdit) {
      this.setGeometryEditButtonVisible(false);
    } else {
      this.setGeometryEditButtonVisible(active);
    }    

    this.geometryActiveChanged.emit({
      id: geometry.id,
      id_orig: geometry.baseItem.id ?? 0,
      active: geometry.activated,
      baseItem: geometry.baseItem,
    });
  }

  private setGeometryPOIActive(geometry: Geometry, poi: POI, active: boolean) {
    if (this.currentlyActiveGeometry && this.currentlyActiveGeometry.id != geometry.id) {
      throw new Error('another geometry is currently active');
    }
    if (this.currentlyActiveGeometryPOI && this.currentlyActiveGeometryPOI.id != poi.id) {
      throw new Error('another poi is currently active');
    }
    poi.activated = active;
    if (poi.activated) {
      poi.marker.zIndex = this.foregroundZIndex;
      poi.marker.element.firstElementChild?.classList.add('activated');
    } else {
      poi.marker.zIndex = this.geometryZIndex + 2;
      poi.marker.element.firstElementChild?.classList.remove('activated');
    }

    if (active) {
      this.currentlyActiveGeometryPOI = poi;
    } else {
      this.currentlyActiveGeometryPOI = undefined;
    }
  }

  private createDeleteMarker(): HTMLElement {
    const content = document.createElement('div');
    content.classList.add('delete-marker');
    content.innerHTML = `
			<span class="material-symbols-outlined">delete</span>
		`;
    return content;
  }

  private createGeometryFromEditingData(): GeometryDto {
    const boundary: BoundaryDto[] = [];
    if (this.editingGeometryBoundaries) {
      for (const polygon of this.editingGeometryBoundaries) {
        const polygonBoundary: GeoCoordinateDto[] = [];
        const holeBoundaries: GeoCoordinateDto[][] = [];
        const polygonPaths = polygon.getPaths();
        for (let pathId = 0; pathId < polygonPaths.getLength(); pathId++) {
          const path = polygonPaths.getAt(pathId);
          if (pathId == 0) {
            for (const point of path.getArray()) {
              polygonBoundary.push({ latitude: point.lat(), longitude: point.lng() });
            }
          } else {
            const holeBoundary: GeoCoordinateDto[] = [];
            for (const point of path.getArray()) {
              holeBoundary.push({ latitude: point.lat(), longitude: point.lng() });
            }
            holeBoundaries.push(holeBoundary);
          }
        }
        boundary.push({ boundary: polygonBoundary, holes: holeBoundaries });
      }
    }

    const pois: POIDto[] = [];
    if (this.editingGeometryPOIs) {
      for (const poi of this.editingGeometryPOIs) {
        pois.push(poi.baseItem);
      }
    }

    const resultGeo: GeometryDto = {
      boundary: boundary,
      pois: pois,
    };

    // add field or location id if available
    if (this.editingGeometryId) {
      const geo: Geometry = this.getGeometry(this.editingGeometryId);
      if (ExaNewMapComponent.isFieldGeometry(geo.baseItem)) {
        resultGeo.field = { id: geo.baseItem.field!.id! };
      } else {
        resultGeo.location = { id: geo.baseItem.location!.id };
      }
    }

    return resultGeo;
  }

  private getPOIById(pois: POI[], poiId: number): POI | undefined {
    for (const poi of pois) {
      if (poi.id == poiId) {
        return poi;
      }
    }
    return undefined;
  }

  addSVGDtoOverlay(mapOverlay: CoverageMapSVGDto | InterpolationMapSVGDto): number {
    const overlay: SVGOverlayOptions = {
      lowerRight: mapOverlay.lowerRight!,
      upperLeft: mapOverlay.upperLeft!,
      svgData: mapOverlay.svgData!,
      visible: true,
    };
    return this.addOverlay(overlay);
  }

  addOverlay(overlay: SVGOverlayOptions): number {
    const id = this.generateUniqueOverlayId();
    this.addOverlay_(id, overlay);
    return id;
  }

  private addOverlay_(id: number, overlay: SVGOverlayOptions) {
    this.checkForMap();
    overlay.visible = overlay.visible ?? this.allOverlaysVisible;
    const overlayOpts: SVGOverlayOptions = {
      map: overlay.visible ? this.map : undefined,
      lowerRight: overlay.lowerRight,
      upperLeft: overlay.upperLeft,
      svgData: overlay.svgData,
      opacity: overlay.opacity,
      visible: overlay.visible,
    };
    const ovrly: ExaSVGOverlay = new ExaSVGOverlay(overlayOpts);
    this.svgOverlays.set(id, { id: id, visible: overlayOpts.visible, overlay: ovrly });
  }

  /*
	updateOverlay(id: number, overlay: SVGOverlayOptions){
		this.checkForMap();
		let visible: boolean = true;
		try{
			// try to delete the old geometry if it is available
			let overlay = this.getOverlay(id);
			visible = overlay.visible;
			this.removeOverlay(overlay);
		}catch(e){
			// ignore
		}
		this.addOverlay_(id, overlay);
	}
	*/

  removeOverlays() {
    for (const id of this.svgOverlays.keys()) {
      this.removeOverlayById(id);
    }
  }

  removeOverlayById(id: number) {
    try {
      const overlay = this.getOverlay(id);
      this.removeOverlay(overlay);
    } catch (e) {
      // ATM do nothing
    }
  }

  private removeOverlay(overlay: SVGOverlay) {
    overlay.overlay.setMap(null);
    this.svgOverlays.delete(overlay.id);
  }

  setOverlayVisible(id: number, visible?: boolean) {
    const overlay = this.getOverlay(id);
    if (visible != undefined) {
      overlay.visible = visible;
    }
    if (overlay.visible && this.allOverlaysVisible) {
      overlay.overlay.setMap(this.map ?? null);
    } else {
      overlay.overlay.setMap(null);
    }
  }

  setOverlayOpacity(id: number, opacity: number) {
    const overlay = this.getOverlay(id);
    overlay.overlay.setOpacity(opacity);
  }

  addEvent(event: EventV2Dto): number {
    const opts: CustomMarkerOptions = {
      visible: true,
      clickable: true,
      label: this.getTranslation('ENUM.EVENTTYPE.' + event.type!),
      data: this.createEventContent(event),
      type: CustomerMarkerType.PIN,
      icon: this.getEventIcon(event),
      color: '#2196F3',
      position: { latitude: event.latitude ?? 0, longitude: event.longitude ?? 0 },
      baseItem: event,
    };
    return this.addCustomMarker(opts);
  }

  setOwnPosition(position: PositionV2Dto, size = 16, color = ExaNewMapComponent.DEFAULT_COLOR) {
    const marker = this.customMarker.get(ExaNewMapComponent.OWN_POSITION_MAKER_ID);
    if (marker && color && color == marker.color && size && size == marker.size) {
      this.updateCustomMarkerPosition(ExaNewMapComponent.OWN_POSITION_MAKER_ID, position);
    } else {
      // create / update the marker
      const opts: CustomMarkerOptions = {
        visible: true,
        visibleAllZoomLevels: true,
        clickable: false,
        data: 'own-position',
        type: CustomerMarkerType.CIRCULAR,
        color: color,
        size: size,
        position: position,
        zIndex: this.ownPositionZIndex,
        baseItem: 'own-position',
      };
      this.updateCustomMarker(ExaNewMapComponent.OWN_POSITION_MAKER_ID, opts);
    }
  }

  removeOwnPosition() {
    this.removeCustomMarkerById(ExaNewMapComponent.OWN_POSITION_MAKER_ID);
  }

  private createEventContent(event: EventV2Dto): string {
    let string = '';
    if (
      event.type == EventTypeEnum.EMPLOYEE_ENTERED_MACHINE ||
      event.type == EventTypeEnum.EMPLOYEE_LEFT_MACHINE ||
      event.type == EventTypeEnum.GRAIN_TANK_OVER_70
    ) {
      string = this.getMachineName(event.machine);
    } else if (
      event.type == EventTypeEnum.EMPLOYEE_COUPLED_IMPLEMENT ||
      event.type == EventTypeEnum.EMPLOYEE_UNCOUPLED_IMPLEMENT ||
      event.type == EventTypeEnum.LOADED_IMPLEMENT ||
      event.type == EventTypeEnum.UNLOADED_IMPLEMENT
    ) {
      string = this.getImplementName(event.implement);
    } else if (event.type == EventTypeEnum.EMPLOYEE_ENTERED_FIELD || event.type == EventTypeEnum.EMPLOYEE_LEFT_FIELD) {
      string = this.getGeometryName(event.geometry);
    } else if (event.type == EventTypeEnum.CUSTOMER) {
      string = event.data ?? '';
    } else if (event.type == EventTypeEnum.WEIGHING) {
      string = this.getMachineName(event.machine) + ' - ' + (event.data ?? '');
    } else if (event.data) {
      string = event.data;
    }
    if (event.unit) {
      string = string.concat(this.getTranslation('ENUM.MSGUNIT.' + event.unit));
    }
    return string;
  }

  addCustomMarker(markerOptions: CustomMarkerOptions, id?: number): number {
    if (!id) {
      id = this.generateUniqueCustomMarkerId();
    }
    this.addCustomMarker_(id, markerOptions);
    return id;
  }

  private addCustomMarker_(id: number, markerOptions: CustomMarkerOptions) {
    this.checkForMap();
    markerOptions.visible = markerOptions.visible ?? this.allCustomMarkerVisible;

    const zIndex = markerOptions.zIndex ?? this.geometryZIndex + 2;
    const cuMaOpts: google.maps.marker.AdvancedMarkerElementOptions = {
      map: markerOptions.visible && (markerOptions.visibleAllZoomLevels || this.showMarkerForZoom(this.currentZoom)) ? this.map : null,
      position: { lat: markerOptions.position.latitude ?? 0, lng: markerOptions.position.longitude ?? 0 },
      content: this.createCustomMarker(markerOptions),
      zIndex: zIndex,
    };
    const cuMa: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement(cuMaOpts);
    let listener: google.maps.MapsEventListener | undefined = undefined;
    if (markerOptions.clickable) {
      // centroid marker clicks are equal to polygon clicks
      listener = cuMa.addListener('click', () => {
        this.customMarkerClickHandler(id);
      });
    }

    const m: CustomMarker = {
      id: id,
      activated: false,
      clickable: markerOptions.clickable,
      visible: markerOptions.visible,
      visibleAllZoomLevels: markerOptions.visibleAllZoomLevels ?? false,
      type: markerOptions.type,
      color: markerOptions.color,
      size: markerOptions.size,
      zIndex: zIndex,
      marker: cuMa,
      markerListener: listener,
      baseItem: markerOptions.baseItem,
    };
    this.customMarker.set(id, m);
  }

  updateCustomMarker(id: number, markerOptions: CustomMarkerOptions) {
    this.checkForMap();
    try {
      const marker = this.getCustomMarker(id);
      this.removeCustomMarker(marker);
    } catch (e) {
      // ATM do nothing
    }
    this.addCustomMarker_(id, markerOptions);
  }

  updateCustomMarkerPosition(id: number, position: PositionV2Dto) {
    this.checkForMap();
    const marker = this.getCustomMarker(id);
    marker.marker.position = { lat: position.latitude ?? 0, lng: position.longitude ?? 0 };
  }

  removeCustomMarkers() {
    for (const id of this.customMarker.keys()) {
      this.removeCustomMarkerById(id);
    }
  }

  removeCustomMarkerById(id: number) {
    try {
      const marker = this.getCustomMarker(id);
      this.removeCustomMarker(marker);
    } catch (e) {
      // ATM do nothing
    }
  }

  private removeCustomMarker(marker: CustomMarker) {
    marker.marker.map = null;
    this.customMarker.delete(marker.id);
  }

  setCustomMarkerVisible(id: number, visible?: boolean) {
    const marker = this.getCustomMarker(id);
    if (visible != undefined) {
      marker.visible = visible;
    }
    if (marker.visible && this.allCustomMarkerVisible) {
      marker.marker.map = this.map ?? null;
    } else {
      marker.marker.map = null;
    }
  }

  // https://developers.google.com/maps/documentation/javascript/advanced-markers/html-markers?hl=de#maps_advanced_markers_html-javascript
  private createMachineMarker(
    machine: MachineV2Dto | MachineIdDto,
    forceCreateNew?: boolean,
    markerNameVisible = true,
    isOffline = false,
    showOwner = false
  ): HTMLElement {
    if (this.machineMarkerMap.has(machine.id!) && !forceCreateNew) {
      return this.machineMarkerMap.get(machine.id!)!;
    }
    // create default marker with no icon -> icon will later be updated
    const marker = this.createMachineMarkerIcon(machine, markerNameVisible, isOffline, showOwner);
    this.machineMarkerMap.set(machine.id!, marker);

    // get the correct icon and update the marker when loaded
    const iconName = this.getMachineIconName(machine);
    void this.getSVGIcon('assets/custom-ionicons/' + iconName + '.svg').then((svgIcon) => {
      this.setTrackMachineIcon(machine.id!, svgIcon);
    });

    return marker;
  }

  private createMachineMarkerIcon(
    machine: MachineV2Dto | MachineIdDto,
    markerNameVisible = true,
    isOffline = false,
    showOwner = false,
    svgIcon?: string
  ): HTMLElement {
    const machineName = this.getMachineName(machine);
    const color = this.getColor(machine);

    let adapterInfo: string | undefined;
    let lastOnline: string | undefined;
    let ownerName: string | undefined;
    if (Object.prototype.hasOwnProperty.call(machine, 'owner')) {
      const machine_ = machine as MachineV2Dto;
      if (machine_.pairedAdapter?.serial) {
        adapterInfo = machine_.pairedAdapter.serial;
      }
      if (machine_.lastPosition?.time) {
        const online: Date = new Date(machine_.lastPosition.time);
        lastOnline = this.formatDate(online, 'dd.MM.yyyy HH:mm');
      }
      if (showOwner) {
        ownerName = machine_.owner?.name;
      }
    } else {
      const machine_ = machine as MachineIdDto;
      if (machine_.adapterSerial) {
        adapterInfo = machine_.adapterSerial;
      }
    }

    const content = document.createElement('div');
    content.id = 'machine-' + (machine.id ?? 0).toString() + '-marker-' + this.id.toString();
    content.classList.add('marker');
    content.classList.add('pin');
    if (isOffline) {
      content.style.color = ExaNewMapComponent.OFFLINE_MARKER_COLOR;
      content.style.backgroundColor = ExaNewMapComponent.OFFLINE_MARKER_COLOR;
    } else {
      content.style.color = color;
      content.style.backgroundColor = color;
    }

    let adapterInfoDIV = '';
    if (adapterInfo) {
      adapterInfoDIV = `
            <div class="icon-value-pair">
              <span class="material-symbols-outlined icon-value-pair-icon">link</span>
              <span class="icon-value-pair-value">${adapterInfo}</span>
            </div>
			    `;
    }

    let lastOnlineDIV = '';
    if (lastOnline) {
      lastOnlineDIV = `
            <div class="icon-value-pair">
              <span class="material-symbols-outlined icon-value-pair-icon">vital_signs</span>
              <span class="icon-value-pair-value"><span id="machine-${machine.id ?? 0}-last-online-${this.id}">${lastOnline}</span></span>
            </div>
			    `;
    }
    let ownerDIV = '';
    if (ownerName) {
      ownerDIV = `
            <div class="icon-value-pair">
              <span class="material-symbols-outlined icon-value-pair-icon">domain</span>
              <span class="icon-value-pair-value"><span id="machine-${machine.id ?? 0}-owner-${this.id}">${ownerName}</span></span>
            </div>
			    `;
    }

    let nameVisibility = '';
    if (!markerNameVisible) {
      nameVisibility = ' hidden';
    }

    content.innerHTML = `
          <div class="label${nameVisibility}" id="machine-${machine.id ?? 0}-name-${this.id}">${machineName}</div>
          <div class="icon" id="machine-${machine.id ?? 0}-icon-${this.id}">
            ${svgIcon ?? ''}
          </div>
          <div class="details">
            <div class="title">
              <div class="title-icon" style="color: ${color} !important; fill: ${color} !important;"  id="machine-${machine.id ?? 0}-icon-details-${this.id}">
                ${svgIcon ?? ''}
              </div>
              <span class="title-label">${machineName}</span>
            </div>
            <div class="data">
              <div class="icon-value-pair">
                <span class="material-symbols-outlined icon-value-pair-icon">info</span>
                <span class="icon-value-pair-value">${machine.make ?? ''} ${machine.model ?? ''}</span>
              </div>
              ${adapterInfoDIV}
              ${lastOnlineDIV}
              ${ownerDIV}
            </div>
          </div>
        `;
    return content;
  }

  private createCentroidMarker(
    id: string,
    name?: string,
    area?: number,
    identifier?: string,
    customer?: EnterpriseCustomerIdDto,
    crop?: string,
    color = ExaNewMapComponent.DEFAULT_COLOR,
    icon = 'space_dashboard',
    forceCreateNew = true
  ): HTMLElement {
    if (this.geometryCentroidMap.has(id) && !forceCreateNew) {
      return this.geometryCentroidMap.get(id)!;
    }

    let identifierDIV = '';
    if (identifier) {
      identifierDIV = `
			<div class="icon-value-pair">
				<span class="material-symbols-outlined icon-value-pair-icon">fingerprint</span>
				<span class="icon-value-pair-value">${identifier}</span>
			</div>
			`;
    }

    let customerDIV = '';
    if (customer) {
      customerDIV = `
			<div class="icon-value-pair">
				<span class="material-symbols-outlined icon-value-pair-icon">person</span>
				<span class="icon-value-pair-value">${this.getCustomerName(customer)}</span>
			</div>
			`;
    }

    let cropDIV = '';
    if (crop) {
      cropDIV = `
			<div class="icon-value-pair">
				<span class="material-symbols-outlined icon-value-pair-icon">waves</span>
				<span class="icon-value-pair-value">${crop}</span>
			</div>
			`;
    }

    const content = document.createElement('div');
    content.classList.add('marker');
    content.classList.add('pin');
    content.style.color = color;
    content.style.backgroundColor = color;
    content.innerHTML = `
			<div class="label">${name ?? ''}</div>
			<div class="icon">
				<span class="material-symbols-outlined">${icon}</span>
			</div>
			<div class="details">
				<div class="title">
					<div class="title-icon" style="color: ${color} !important; fill: ${color} !important;">
						<span class="material-symbols-outlined">${icon}</span>
					</div>
					<span class="title-label">${name ?? ''}</span>
				</div>
				<div class="data">
					${identifierDIV}
					<div class="icon-value-pair">
						<span class="material-symbols-outlined icon-value-pair-icon">crop_square</span>
						<span class="icon-value-pair-value">${this.formatDecimal(area, '0.2-2')} ha</span>
					</div>
					${customerDIV}
          ${cropDIV}
				</div>
			</div>
		`;

    this.geometryCentroidMap.set(id, content);

    return content;
  }

  private createCustomMarker(markerOptions: CustomMarkerOptions): HTMLElement {
    let iconName = undefined;
    if (markerOptions.icon) {
      iconName = markerOptions.icon;
    }
    let color = ExaNewMapComponent.DEFAULT_COLOR;
    if (markerOptions.color) {
      color = markerOptions.color;
    }

    let iconDIV = '';
    if (markerOptions.icon && iconName) {
      iconDIV = `
			<div class="icon">
				<span class="material-symbols-outlined">${iconName}</span>
			</div>
			`;
    }
    let titleIconDIV = '';
    if (markerOptions.icon && iconName) {
      titleIconDIV = `
      <div class="title-icon" style="color: ${color} !important; fill: ${color} !important;">
        <span class="material-symbols-outlined">${iconName}</span>
      </div>
			`;
    }

    const content = document.createElement('div');
    content.classList.add('marker');
    if (markerOptions.type == CustomerMarkerType.CIRCULAR) {
      if (markerOptions.icon) {
        content.classList.add('circular');
      } else {
        content.classList.add('circular-small');
        if (markerOptions.size) {
          content.style.width = markerOptions.size.toString() + 'px';
          content.style.height = markerOptions.size.toString() + 'px';
          content.style.top = Math.floor(markerOptions.size / 2).toString() + 'px';
          content.style.borderWidth = Math.floor(markerOptions.size / 8).toString() + 'px';
        }
      }
    } else {
      content.classList.add('pin');
    }
    content.style.color = color;
    content.style.backgroundColor = color;
    content.innerHTML = `
			<div class="label">${markerOptions.label ?? ''}</div>
      ${iconDIV}
			<div class="details">
				<div class="title">
          ${titleIconDIV}
					<span class="title-label">${markerOptions.label ?? ''}</span>
				</div>
				<div class="data">
					<div class="icon-value-pair">
						<span class="material-symbols-outlined icon-value-pair-icon">description</span>
						<span class="icon-value-pair-value">${markerOptions.data ?? ''}</span>
					</div>
				</div>
			</div>
		`;
    return content;
  }

  private getLocationIconName(location: LocationV2Dto): string {
    switch (location.type) {
      case LocationTypeEnum.FARM:
        return 'home';
      case LocationTypeEnum.FUELSTATION:
        return 'local_gas_station';
      case LocationTypeEnum.STABLE:
        return 'warehouse';
      case LocationTypeEnum.WEIGHINGSTATION:
        return 'weight';
      default:
        return 'flag';
    }
  }

  private createPOIMarker(poi?: POIDto): HTMLElement {
    const iconName = this.getPOIIconName(poi);
    const color = this.getPOIColor(poi);
    const content = document.createElement('div');
    const name = this.getPOIName(poi);
    content.classList.add('marker');
    content.classList.add('pin');
    content.style.color = color;
    content.style.backgroundColor = color;
    content.innerHTML = `
			<div class="label">${name ?? ''}</div>
			<div class="icon">
				<span class="material-symbols-outlined">${iconName}</span>
			</div>
			<div class="details">
				<div class="title">
					<div class="title-icon" style="color: ${color} !important; fill: ${color} !important;">
						<span class="material-symbols-outlined">${iconName}</span>
					</div>
					<span class="title-label">${name ?? ''}</span>
				</div>
				<div class="data">
					<span>${poi?.comment ?? ''}</span>
				</div>
			</div>
		`;
    return content;
  }

  private getPOIName(poi?: POIDto): string {
    if (poi?.type) {
      return this.getTranslation('MARKER.' + poi.type);
    }
    return '';
  }

  private getPOIIconName(poi?: POIDto): string {
    if (!poi) {
      return 'info'; // exa green
    }
    switch (poi.type) {
      case MarkerTypeEnum.WARNING:
        return 'warning';
      case MarkerTypeEnum.ENTRANCE:
        return 'login';
      case MarkerTypeEnum.EXIT:
        return 'logout';
      default:
        return 'info';
    }
  }

  private getPOIColor(poi?: POIDto): string {
    if (!poi) {
      return ExaNewMapComponent.DEFAULT_COLOR; // exa green
    }
    switch (poi.type) {
      case MarkerTypeEnum.WARNING:
        return '#dd0000';
      case MarkerTypeEnum.INFO:
        return '#0d73d9';
      default:
        return ExaNewMapComponent.DEFAULT_COLOR; // exa green
    }
  }

  private getSVGIcon(url: string): Promise<string> {
    if (this.svgIconMap.has(url)) {
      return Promise.resolve(this.svgIconMap.get(url)!);
    }
    return new Promise<string>((resolve, reject) => {
      const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
      void firstValueFrom(this.httpClient.get(url, { headers, responseType: 'text' })).then((data) => {
        this.svgIconMap.set(url, data);
        resolve(data);
      });
    });
  }

  public static isFieldGeometry(geometry: GeometryDto): boolean {
    return geometry?.field != undefined;
  }

  private getColorGeometry(geometry: GeometryDto, defaultColor = '#000000'): string {
    if (geometry?.customer) {
      return this.getColor(geometry.customer);
    }
    return defaultColor;
  }
}
